import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Camera, Star, Stethoscope, UploadSimple, FloppyDisk, Spinner,
    User as UserIcon, Buildings, UsersThree,
    Clock, Bell as BellIcon, Lock, ShieldWarning,
    Envelope, ChatCircleDots, Check,
    Wallet, PencilSimple, Phone,
    Scan, Sparkle, CheckCircle, X as XIcon, Info,
    Plus, Warning, ShareNetwork, Certificate,
    PaperPlaneTilt, MagnifyingGlass, UserPlus, Gift,
    IdentificationCard, Briefcase, DeviceMobile, House,
} from "@phosphor-icons/react";
import { toast } from "sonner";
// Embedded managers — both used to be standalone pages at /doctor/practice-locations
// and /doctor/clinic-staff, now they live inside the Profile Locations + Team tabs
// so the doctor never leaves the Profile shell for these CRUD flows.
import PracticeLocationsManager from "@/pages/doctor/PracticeLocations";
import ClinicStaffManager from "@/pages/doctor/ClinicStaff";
import NudgeTemplatesEditor from "@/components/doctor/NudgeTemplatesEditor";
import ClinicPatientCardPreview from "@/components/doctor/ClinicPatientCardPreview";
import SignatureField from "@/components/doctor/SignatureField";
import SandboxLinkGenerator from "@/components/sandbox/SandboxLinkGenerator";
import ReferralRewardsCard from "@/components/doctor/ReferralRewardsCard";
import CredentialsSection from "@/components/doctor/CredentialsSection";
import PhoneInput10 from "@/components/site/PhoneInput10";
import DoctorNameInput, { stripDrPrefix } from "@/components/site/DoctorNameInput";
import { toE164, to10Digit, isValid10Digit } from "@/lib/phone";
import TimeSlotSelect from "@/components/common/TimeSlotSelect";
// iter97.79 — Reuse the case-wizard ExamAccordion shell so the Doctor
// Profile reads like one consistent accordion-driven settings surface
// (matches the Step 2 clinical exam pattern doctors already know).
import { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";

/* iter100.35 — DAY_KEYS constant removed. Was only used by the deleted
   "Available days" chip row. Business hours (Hours & Contact tab) is
   now the single source of truth for weekly availability. */

const EMPTY = {
    name: "", specialization: "", clinic_name: "", city: "", address: "", bio: "",
    // iter100.318 — Structured location fields (previously bundled into
    // free-form `address`). `area` powers the "Area, City" line on
    // Find-a-Doctor cards. `pincode` is a 6-digit optional field.
    area: "", pincode: "",
    // iter100.319 — Certifications & Achievements. Array of
    // {id, title, issuer, year, kind ∈ {certification|award|publication}}
    credentials: [],
    // iter100.319 — Read-only server metadata used by the Credentials
    // accordion for auto-badges. Never sent on save.
    _lifecycle_status: "",
    _dci_registration_number: "",
    _state_council: "",
    qualifications: "", years_experience: "", practice_start_year: "", consultation_fee: "",
    phone: "", whatsapp_phone: "", email: "",
    // iter100.283 — Public patient-facing contact numbers. Live on the
    // doctor's landing page CTAs (Call / WhatsApp). Blank = fall back
    // to `phone` / `whatsapp_phone` above.
    landing_cta_phone: "", landing_cta_whatsapp: "",
    languages: "",
    // iter100.35 — `available_days` removed from form state (was only
    // read by the deleted chip row). Business hours in Hours & Contact
    // is now the sole weekly-availability source of truth.
    // Clinic billing identity — printed on every invoice.
    clinic_phone: "", clinic_address: "", gstin: "", billing_footer: "",
    // iter101.93 — Auto-billing fields removed. Doctor→patient invoicing
    // is 100% manual now; the doctor issues fee invoices themselves from
    // the patient's Billing tab. Only the no-show grace + auto-no-show
    // status toggle remain (they don't move money — just labels).
    no_show_grace_minutes: "",
    auto_no_show_enabled: true,
    // Business hours (per day-of-week). UI shape mirrors backend:
    //   { mon: { open: "09:00", close: "18:00", closed: false }, ... }
    // Empty/null on load → assumed Mon-Sat 09:00-18:00, Sun closed.
    business_hours: {},
    // Bank / payout details. Plain text; finance reconciles by hand for now.
    bank_account_name: "", bank_account_number: "", bank_ifsc: "",
    bank_name: "", upi_id: "",
    // iter72 — Phase B clinic branding. Drives the patient care card PDF
    // header + email signatures (future) + invoice header (future). All
    // optional; when blank, the PDF falls back to Invisique master brand.
    clinic_logo_data_url: "",
    clinic_letterhead: "",
    dci_registration_number: "",
    after_hours_line: "",
    doctor_signature_data_url: "",
    // Notification preferences. Shape:
    //   { plan_ready: {in_app, email, whatsapp}, ... }
    notification_preferences: {},
    // iter97.81 — Intraoral scanning declaration. Empty string ≡ "not
    // declared yet". Captures clinic-side scanner ownership for smart
    // wizard defaults + Invisique IO Scanning Service subscriptions.
    // ("considering" choice removed in iter97.81 — collapsed into the
    // 2-state Yes/No primary picker.)
    in_house_scanner: "",  // "yes" | "no"
    scanner_brand: "",
    scanner_model: "",
    scanner_count: "",
    scanner_trained_staff_count: "",
    scanner_export_format: "",
    scanner_calibration_cadence: "",
    scanner_notes: "",
    subscribed_to_io_scanning_service: false,
    io_scanning_subscribed_at: "",
    io_scanning_service_notes: "",
};


/* Defaults for business hours when the doctor has nothing saved yet. */
const DEFAULT_HOURS = {
    mon: { open: "09:00", close: "18:00", closed: false },
    tue: { open: "09:00", close: "18:00", closed: false },
    wed: { open: "09:00", close: "18:00", closed: false },
    thu: { open: "09:00", close: "18:00", closed: false },
    fri: { open: "09:00", close: "18:00", closed: false },
    sat: { open: "09:00", close: "14:00", closed: false },
    sun: { open: "09:00", close: "14:00", closed: true },
};


/* Notification topics + channels. Each topic has 3 channels — in_app
   defaults true (the bell + sidebar badges), email + whatsapp default
   false. Doctor toggles per-topic per-channel. */
const NOTIFICATION_TOPICS = [
    { key: "plan_ready",            label: "Plan ready to review",      hint: "When your planner sends a treatment plan for your approval" },
    { key: "info_request",          label: "Question from planner",     hint: "When the COE team needs info to continue planning" },
    { key: "lab_chat",              label: "Lab chat message",          hint: "New message on any of your case threads" },
    { key: "patient_chat",          label: "Patient message",           hint: "When a patient writes to your clinic" },
    { key: "billing",               label: "Invoice activity",          hint: "Invoices paid, overdue, or sent" },
    { key: "appointment_reminders", label: "Appointment reminders",     hint: "Day-before and same-day reminders" },
];


const DEFAULT_NOTIF_PREFS = NOTIFICATION_TOPICS.reduce((acc, t) => {
    acc[t.key] = { in_app: true, email: false, whatsapp: false };
    return acc;
}, {});

/* Section anchor map for the sticky left-rail TOC. Each entry's `id` MUST
   match the corresponding <Section id="…"> in the form. Order = the order
   shown in the sidebar and the natural fill-out flow for a new doctor. */
const PROFILE_SECTIONS = [
    // 2026-02-28 — merged: "Personal" + "Booking" → single "Profile" tab.
    // Both define the public-facing identity (bio + fee + availability),
    // so they share a mental block. Internal section headers preserved.
    // 2026-02-28 (iter 64) — additionally folded "Billing & Payouts" into
    // the Profile tab per user feedback. Profile is now the doctor's full
    // clinic-identity surface: who they are + how they bill + how they
    // get paid. Hours & Contact stays separate (it's operational, not
    // identity). All Save commits are still done in one PUT.
    // iter100.40 — Hours & Contact tab merged into Profile tab. Its two
    // accordions (Contact + Business hours) are patient-facing identity
    // data (same category as Name / Qualifications / Fee) — belonged in
    // Profile all along. Tab strip trimmed from 6 → 5 tabs. Legacy
    // `?tab=hours_contact` URL still remaps to Profile via `TAB_REMAP`.
    { id: "profile",         label: "Profile",           icon: UserIcon },
    // 2026-02-28 — merged: "Notifications" + "Security" → "Account" tab.
    // Both are app-level preferences (not clinic ops), and each was light
    // enough that splitting them inflated the tab count without payoff.
    { id: "account",         label: "Account",           icon: BellIcon },
    // 2026-02-28 — Wave 5: doctor-owned WhatsApp nudge template editor.
    // Lets the clinic customize the voice of the 3 auto-nudge templates
    // (low compliance / open issue / no recent log) without touching code.
    { id: "nudges",          label: "Nudge templates",   icon: ChatCircleDots },
    { id: "locations",       label: "Locations",         icon: Buildings },
    { id: "team",            label: "Clinic staff",      icon: UsersThree },
    // iter100.175 — Refer & Earn is now a proper tab (was: inline
    // section above tabs). Semantically fits the profile page — a
    // personal growth action alongside the doctor's identity & clinic
    // settings. Deep-linked from the Dashboard Quick Actions "Refer"
    // tile via ?tab=refer.
    { id: "refer",           label: "Refer & Earn",      icon: ShareNetwork },
];

export default function DoctorProfile() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState(EMPTY);
    const [original, setOriginal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoUrl, setPhotoUrl] = useState("");
    const [stats, setStats] = useState({ rating: 0 });
    const fileRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    // iter100.266 — Lifecycle completeness (11-check "must-haves for
    // going live") shared with the dashboard chip. Overrides the older
    // section-based `overallCompletion` so both surfaces always report
    // the same number.
    const [lifecycleComp, setLifecycleComp] = useState(null);

    const loadLifecycle = async () => {
        try {
            const { data } = await api.get("/doctor/me/onboarding");
            setLifecycleComp(data?.completeness || null);
        } catch { /* non-fatal — Profile still renders with old section calc */ }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/doctor/profile");
            const next = {
                name: stripDrPrefix(data.name || ""), specialization: data.specialization || "",
                clinic_name: data.clinic_name || "", city: data.city || "",
                address: data.address || "", bio: data.bio || "",
                // iter100.318 — Structured area + pincode fields.
                area: data.area || "", pincode: data.pincode || "",
                // iter100.319 — Certifications / awards / publications.
                credentials: Array.isArray(data.credentials) ? data.credentials : [],
                // iter100.319 — Read-only server signals for auto-badges.
                _lifecycle_status: data.lifecycle_status || "",
                _dci_registration_number: data.dci_registration_number || data.council_registration_number || "",
                _state_council: data.state_council || "",
                // iter100.68 — Doctor's join date. Read-only, surfaced on
                // the live Member Card preview as "MEMBER SINCE <MMM YYYY>".
                _created_at: data.created_at || "",
                qualifications: data.qualifications || "",
                years_experience: data.years_experience ?? "",
                practice_start_year: data.practice_start_year ?? "",
                consultation_fee: data.consultation_fee ?? "",
                phone: to10Digit(data.phone || ""), whatsapp_phone: to10Digit(data.whatsapp_phone || ""), email: data.email || "",
                languages: (data.languages || []).join(", "),
                // Clinic billing identity — printed on every invoice.
                clinic_phone: to10Digit(data.clinic_phone || ""),
                clinic_address: data.clinic_address || "",
                gstin: data.gstin || "",
                billing_footer: data.billing_footer || "",
                // iter101.93 — Cancellation fee + auto-billing fields
                // removed. Only no-show grace + auto-no-show status
                // toggle remain (labels only, no money movement).
                no_show_grace_minutes: data.no_show_grace_minutes ?? "",
                auto_no_show_enabled: data.auto_no_show_enabled !== false,
                // New sections — fall back to sensible defaults when the
                // doctor hasn't set anything yet.
                // iter100.65 — Also backfill any partial rows on load so
                // legacy corrupted data (missing `close` field) gets
                // normalized on the next save. Every row is guaranteed
                // to be `{open, close, closed}` after this transform.
                business_hours: (() => {
                    const raw = (data.business_hours && Object.keys(data.business_hours).length)
                        ? data.business_hours
                        : DEFAULT_HOURS;
                    const normalized = {};
                    for (const [day, row] of Object.entries(raw)) {
                        if (!row || typeof row !== "object") continue;
                        normalized[day] = {
                            open: row.open || "09:00",
                            close: row.close || "18:00",
                            closed: Boolean(row.closed),
                        };
                    }
                    return normalized;
                })(),
                bank_account_name: data.bank_account_name || "",
                bank_account_number: data.bank_account_number || "",
                bank_ifsc: data.bank_ifsc || "",
                bank_name: data.bank_name || "",
                upi_id: data.upi_id || "",
                clinic_logo_data_url: data.clinic_logo_data_url || "",
                clinic_letterhead: data.clinic_letterhead || "",
                dci_registration_number: data.dci_registration_number || "",
                after_hours_line: data.after_hours_line || "",
                doctor_signature_data_url: data.doctor_signature_data_url || "",
                notification_preferences: { ...DEFAULT_NOTIF_PREFS, ...(data.notification_preferences || {}) },
                // iter97.81 — Intraoral scanning declaration. Subscribe
                // toggle drives a per-scan-billed service; the timestamp is
                // backend-stamped on first subscribe (display only).
                in_house_scanner: data.in_house_scanner || "",
                scanner_brand: data.scanner_brand || "",
                scanner_model: data.scanner_model || "",
                scanner_count: data.scanner_count ?? "",
                scanner_trained_staff_count: data.scanner_trained_staff_count ?? "",
                scanner_export_format: data.scanner_export_format || "",
                scanner_calibration_cadence: data.scanner_calibration_cadence || "",
                scanner_notes: data.scanner_notes || "",
                subscribed_to_io_scanning_service: !!data.subscribed_to_io_scanning_service,
                io_scanning_subscribed_at: data.io_scanning_subscribed_at || "",
                io_scanning_service_notes: data.io_scanning_service_notes || "",
            };
            setForm(next);
            setOriginal(next);
            setPhotoUrl(data.image || "");
            setStats({ rating: data.rating || 0 });
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); loadLifecycle(); }, []);

    const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    /* iter100.35 — `toggleDay` (available_days chip toggler) removed.
       `available_days` is superseded by `business_hours` — a day with
       time ranges defined is available, else not. See Hours & Contact. */

    /* Strip the in-memory form into the shape the backend update accepts. */
    const buildPayload = () => ({
        // Re-add the "Dr." prefix the UI strips for display, so the
        // doctor record stays "Dr. Riya Kapoor" (what /find-doctor renders).
        name: (() => {
            const bare = stripDrPrefix(form.name).trim();
            return bare ? `Dr. ${bare}` : "";
        })(),
        specialization: form.specialization.trim(),
        clinic_name: form.clinic_name.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        // iter100.318 — Structured location fields.
        area: form.area.trim(),
        pincode: form.pincode.trim(),
        // iter100.319 — Credentials array (already normalized on entry).
        credentials: form.credentials,
        bio: form.bio.trim(),
        qualifications: form.qualifications.trim(),
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        // iter100.320 — Send `practice_start_year`; the backend derives
        // `years_experience` from it on every save so year-of-experience
        // auto-advances each calendar year without doctor intervention.
        practice_start_year: form.practice_start_year === "" ? null : Number(form.practice_start_year),
        consultation_fee: form.consultation_fee === "" ? null : Number(form.consultation_fee),
        phone: form.phone ? (toE164(form.phone) || form.phone.trim()) : "",
        whatsapp_phone: form.whatsapp_phone ? (toE164(form.whatsapp_phone) || form.whatsapp_phone.trim()) : "",
        email: form.email.trim(),
        languages: form.languages.split(",").map((l) => l.trim()).filter(Boolean),
        // iter100.35 — `available_days` removed from the update payload
        // (deprecated in favor of business_hours as the sole availability
        // source of truth). Backend keeps the field on the model for
        // legacy compatibility but nothing reads it downstream.
        clinic_phone: form.clinic_phone ? (toE164(form.clinic_phone) || form.clinic_phone.trim()) : "",
        clinic_address: form.clinic_address.trim(),
        gstin: form.gstin.trim(),
        billing_footer: form.billing_footer.trim(),
        // iter101.93 — cancellation_window_hours / late_cancel_fee /
        // no_show_fee / fee_overrides_by_type dropped. Only no-show
        // grace + auto-no-show status toggle remain in the payload.
        no_show_grace_minutes: form.no_show_grace_minutes === "" ? null : Number(form.no_show_grace_minutes),
        auto_no_show_enabled: Boolean(form.auto_no_show_enabled),
        business_hours: form.business_hours,
        bank_account_name: form.bank_account_name.trim(),
        bank_account_number: form.bank_account_number.trim(),
        bank_ifsc: form.bank_ifsc.trim().toUpperCase(),
        bank_name: form.bank_name.trim(),
        upi_id: form.upi_id.trim(),
        // iter72 — Phase B clinic branding fields.
        clinic_logo_data_url: form.clinic_logo_data_url || "",
        clinic_letterhead: form.clinic_letterhead.trim(),
        dci_registration_number: form.dci_registration_number.trim(),
        after_hours_line: form.after_hours_line.trim(),
        doctor_signature_data_url: form.doctor_signature_data_url || "",
        notification_preferences: form.notification_preferences,
        // iter97.81 — Intraoral scanning declaration. Subscribe toggle is
        // the only writable flag for the No path. `io_scanning_subscribed_at`
        // is backend-stamped (read-only from the form), so we don't echo it
        // back on Save.
        in_house_scanner: form.in_house_scanner || null,
        scanner_brand: form.scanner_brand.trim(),
        scanner_model: form.scanner_model.trim(),
        scanner_count: form.scanner_count === "" ? null : Number(form.scanner_count),
        scanner_trained_staff_count: form.scanner_trained_staff_count === "" ? null : Number(form.scanner_trained_staff_count),
        scanner_export_format: form.scanner_export_format || "",
        scanner_calibration_cadence: form.scanner_calibration_cadence || "",
        scanner_notes: form.scanner_notes.trim(),
        subscribed_to_io_scanning_service: Boolean(form.subscribed_to_io_scanning_service),
        io_scanning_service_notes: form.io_scanning_service_notes.trim(),
    });

    const save = async () => {
        if (form.name.trim().length < 2) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const { data } = await api.put("/doctor/profile", buildPayload());
            toast.success("Profile updated");
            // iter97.81 — Sync backend-stamped fields back into form state so
            // the UI reflects them immediately (e.g. `io_scanning_subscribed_at`
            // is stamped server-side on first subscribe — without this merge
            // the "Subscribed · {date}" chip would only appear on next reload).
            const next = {
                ...form,
                io_scanning_subscribed_at: data.io_scanning_subscribed_at || form.io_scanning_subscribed_at || "",
            };
            setForm(next);
            setOriginal(next);
            setStats({ rating: data.rating || 0 });
            // iter100.266 — Recompute lifecycle completeness so the ring +
            // dashboard chip both update immediately after save.
            loadLifecycle();
            // Refresh sidebar avatar if name/clinic changed
            try {
                const me = await api.get("/auth/me");
                setUser(me.data);
            } catch (err) { console.debug("Profile save: auth/me refresh skipped:", err); }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update profile");
        } finally { setBusy(false); }
    };

    const cancel = async () => {
        if (!original) return;
        // iter100.288 — Confirm before wiping in-flight edits. A stray click
        // on Discard used to instantly blow away 5-10 minutes of typing
        // with no undo. Now we ask once — same pattern most modern
        // editors use.
        const ok = await confirmDialog({
            title: "Discard unsaved changes?",
            message: "Your recent edits will be reverted to their last saved values. This can't be undone.",
            confirmText: "Yes, discard",
            cancelText: "Keep editing",
            tone: "danger",
            testid: "profile-discard-confirm",
        });
        if (ok) setForm(original);
    };

    const handlePhotoFile = async (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Use JPEG, PNG or WebP"); return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image must be under 2 MB"); return;
        }
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        try {
            const { data } = await api.post("/doctor/profile/photo", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotoUrl(data.image);
            // Refresh the sidebar avatar by reloading /auth/me into the AuthContext
            try {
                const me = await api.get("/auth/me");
                setUser(me.data);
            } catch (err) { console.debug("Photo upload: auth/me refresh skipped:", err); }
            toast.success("Photo updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Photo upload failed");
        } finally { setUploading(false); }
    };

    const isDirty = original && JSON.stringify(form) !== JSON.stringify(original);
    // Active tab — defaults to Profile. `?tab=locations` / `?tab=team`
    // deep-links land directly on the right tab (used by the legacy
    // /doctor/practice-locations and /doctor/clinic-staff redirects).
    //
    // 2026-02-28 — back-compat: old links (?tab=contact / ?tab=hours /
    // ?tab=billing / ?tab=payouts / ?tab=personal / ?tab=booking /
    // ?tab=notifications / ?tab=security) re-route to the merged tab
    // they now live in. Avoids stranding bookmarked emails or older
    // support docs.
    const [searchParams, setSearchParams] = useSearchParams();
    const allowedTabs = new Set(PROFILE_SECTIONS.map((s) => s.id));
    const LEGACY_TAB_REMAP = {
        // iter100.40 — Hours & Contact merged into Profile. Old URL
        // still resolves to the same accordions (Contact + Business
        // hours are now inside the Profile tab).
        hours_contact:   "profile",
        contact:         "profile",
        hours:           "profile",
        billing:         "profile",
        payouts:         "profile",
        billing_payouts: "profile",
        personal:        "profile",
        booking:         "profile",
        notifications:   "account",
        security:        "account",
    };
    const urlTabRaw = searchParams.get("tab");
    const urlTab = LEGACY_TAB_REMAP[urlTabRaw] || urlTabRaw;
    const [tab, setTabState] = useState(allowedTabs.has(urlTab) ? urlTab : "profile");
    const setTab = (next) => {
        setTabState(next);
        // Mirror the active tab into the URL so a refresh / bookmark / share
        // restores the same view. `personal` is the default so we omit it.
        const params = new URLSearchParams(searchParams);
        if (next === "personal") params.delete("tab");
        else params.set("tab", next);
        setSearchParams(params, { replace: true });
    };

    /* Tabs with a Save footer. Locations & Clinic Staff tabs render
       their own CRUD UIs (PracticeLocationsManager / ClinicStaffManager);
       no Save button is needed there. */
    const FORM_TABS = new Set([
        "profile", "account",
    ]);

    /* iter97.79 — Single-open accordion state per form tab. iter97.86 —
       All accordions start closed; the doctor opens exactly the one
       they want to edit. Click another → previous closes, new one
       opens (single-open semantics). */
    const [openProfileAcc, setOpenProfileAcc] = useState(null);
    const [openHoursAcc, setOpenHoursAcc] = useState(null);
    const [openAccountAcc, setOpenAccountAcc] = useState(null);

    /* iter100.64 — Deep-link support for the dashboard's "Set weekly hours"
       nudge. When the user lands on this page with `#business-hours` in
       the URL, we open the Business Hours accordion AND scroll it into
       view so the doctor arrives already inside the edit surface. */
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.location.hash !== "#business-hours") return;
        setTabState("profile");
        setOpenProfileAcc("hours");
        const t = setTimeout(() => {
            const el = document.getElementById("business-hours");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
        return () => clearTimeout(t);
    }, []);

    /* iter100.175 — Deep-link support for the Dashboard "Refer" Quick
       Action. Navigating to /doctor/profile#refer switches to the
       Refer & Earn tab (kept alongside the ?tab=refer URL form for
       backward compat with any bookmarks). */
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.location.hash !== "#refer") return;
        setTabState("refer");
    }, []);

    /* iter97.79 — Fill counts per accordion. Drives the X/Y badge in the
       trigger so the doctor knows at a glance which sections still need
       attention. Booleans / structured editors (business_hours, notif_prefs)
       skip the badge entirely (total=undefined). */
    const fills = useMemo(() => {
        const c = (...vals) => vals.filter(present).length;
        // iter97.81 — Scanning accordion fill rules. YES → counts the
        // captured scanner detail fields (target = 6 incl. the primary
        // answer). NO → 2/2 once the doctor has both picked NO and made
        // a subscribe decision (toggling the subscription flag any way
        // counts the second point — declining the service is a valid
        // declaration, not a "not yet decided"). Undeclared → 0/2.
        let scanningFilled = 0, scanningTotal = 2;
        if (form.in_house_scanner === "yes") {
            scanningTotal = 6;
            scanningFilled = 1 /* primary */ + c(form.scanner_brand, form.scanner_model, form.scanner_export_format, form.scanner_calibration_cadence)
                + ((form.scanner_count !== "" && form.scanner_count != null) ? 1 : 0);
        } else if (form.in_house_scanner === "no") {
            scanningFilled = 1 + (form.subscribed_to_io_scanning_service ? 1 : 0);
        }
        return {
            // iter100.36 — Personal now includes consultation fee + years of
            // experience (was previously the standalone "Booking & fee"
            // accordion). Deleted iter100.33 "bio" from the count since the
            // field itself was removed.
            // iter100.37 — DCI registration number + E-Signature moved here
            // from the (renamed) "PDF branding" accordion — they're
            // professional credentials, not print branding. Total now 8.
            personal: { filled: c(form.name, form.specialization, form.qualifications, form.languages, form.consultation_fee, form.practice_start_year, form.dci_registration_number, form.doctor_signature_data_url), total: 8 },
            scanning: { filled: scanningFilled, total: scanningTotal },
            // iter100.38 — "Invoices & PDF branding" (was "Tax & invoice
            // settings" + separate "PDF branding" accordion). Now holds
            // GSTIN + invoice footer + clinic letterhead + clinic logo
            // in one coherent unit with the live ClinicPatientCardPreview.
            billing:  { filled: c(form.gstin, form.billing_footer, form.clinic_letterhead, form.clinic_logo_data_url), total: 4 },
            bank:     { filled: c(form.bank_account_name, form.bank_account_number, form.bank_ifsc, form.bank_name, form.upi_id), total: 5 },
            // iter100.38 — `fills.branding` retired. Branding fields are
            // now counted under `fills.billing`.
            contact:  { filled: c(form.phone, form.whatsapp_phone, form.email, form.clinic_name, form.city, form.address, form.after_hours_line), total: 7 },
            hours:    {
                // iter100.65 — Correct schema check. `business_hours` is
                // `{mon: {open, close, closed}, ...}` — NOT arrays. The
                // old `Array.isArray(ranges)` check always returned 0
                // and made every doctor see "0/7 filled" + the
                // "You haven't set any hours" warning even after saving.
                filled: Object.entries(form.business_hours || {}).filter(([, cfg]) =>
                    cfg && typeof cfg === "object" && (cfg.closed || (cfg.open && cfg.close))
                ).length,
                total:  7,
                // iter100.66 — Track "actually open" days separately from
                // "decided" days. The header subtitle needs the OPEN count
                // (not the decided count) — otherwise a doctor who marked
                // 6 days CLOSED and 1 day OPEN sees "Open 7 days a week".
                openDays: Object.entries(form.business_hours || {}).filter(([, cfg]) =>
                    cfg && typeof cfg === "object" && !cfg.closed && cfg.open && cfg.close
                ).length,
            },
        };
    }, [form]);

    /* iter100.266 — Overall profile completeness now uses the SAME 11-
       check lifecycle system that powers the dashboard chip. Ring % +
       "N sections have gaps" text always match what the doctor sees on
       their dashboard. The older section-based fill count is kept as a
       fallback (in case /doctor/me/onboarding hasn't loaded yet) and to
       power per-accordion "n/m filled" chips. */
    const overallCompletion = useMemo(() => {
        // Prefer lifecycle score when available — single source of truth.
        if (lifecycleComp) {
            const missingCount = (lifecycleComp.missing || []).length;
            return { pct: lifecycleComp.score ?? 0, gaps: missingCount };
        }
        // Fallback: legacy section-based count (shown briefly on first paint).
        const sections = Object.values(fills);
        const totalFields = sections.reduce((s, f) => s + (f.total || 0), 0);
        const filledFields = sections.reduce((s, f) => s + (f.filled || 0), 0);
        if (totalFields === 0) return { pct: 0, gaps: 0 };
        const gaps = sections.filter((f) => f.total > 0 && f.filled < f.total).length;
        return { pct: Math.round((filledFields / totalFields) * 100), gaps };
    }, [fills, lifecycleComp]);
    const completionPct = overallCompletion.pct;

    if (loading) {
        return <div className="text-center text-muted-foreground py-16 text-sm">Loading profile…</div>;
    }

    return (
        <>
        <div data-testid="doctor-profile-page" className="space-y-5 max-w-5xl pb-24">
            {/* HEADER */}
            <div>
                <h1 className="font-display text-2xl font-bold tracking-tight">My Profile</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    How patients see you on the Find an Orthodontist page, your booking confirmation, and case communications.
                </p>
            </div>

            {/* iter100.265 — Standalone rose "E-signature required" banner
                retired. E-signature is now check #11 in the unified
                completeness system (see /doctor/profile main banner +
                the dashboard onboarding chip). The case-submission
                backend guard is unchanged — you still cannot submit a
                case without a signature. */}

            {/* PROFILE HERO — photo · identity · completeness ring */}
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col md:flex-row gap-6 items-start" data-testid="profile-hero">
                {/* Avatar with drag-drop uploader */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                    <label
                        htmlFor="profile-photo-input"
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            handlePhotoFile(e.dataTransfer.files?.[0]);
                        }}
                        className={`group relative cursor-pointer w-24 h-24 rounded-full overflow-hidden bg-secondary flex items-center justify-center border-2 border-dashed transition ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-primary/5"}`}
                        data-testid="profile-avatar-dropzone"
                    >
                        {photoUrl ? (
                            <img src={photoUrl} alt={form.name} className="w-full h-full object-cover" />
                        ) : (
                            <Camera size={22} className="text-muted-foreground" />
                        )}
                        {uploading && (
                            <div className="absolute inset-0 bg-black/40 grid place-items-center text-white">
                                <Spinner size={22} className="animate-spin" />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <span className="text-white text-[10px] font-bold tracking-wider uppercase flex items-center gap-1">
                                <UploadSimple size={11} weight="bold" /> Change
                            </span>
                        </div>
                        <input
                            id="profile-photo-input"
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            data-testid="profile-photo-input"
                            onChange={(e) => handlePhotoFile(e.target.files?.[0])}
                        />
                    </label>
                    <div className="text-[10px] text-muted-foreground text-center max-w-[110px] leading-tight">
                        Click or drag a photo.<br/>JPG/PNG/WebP, max 2 MB.
                    </div>
                </div>

                {/* Identity strip */}
                <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2">
                        <h2 className="font-display text-xl font-bold tracking-tight truncate">{form.name ? `Dr. ${form.name}` : "Your name"}</h2>
                        {stats.rating ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 text-yellow-800 px-2 py-0.5 text-[10px] font-bold">
                                <Star size={10} weight="fill" /> {stats.rating.toFixed(1)}
                            </span>
                        ) : null}
                    </div>
                    <div className="text-sm text-primary font-medium inline-flex items-center gap-1.5">
                        <Stethoscope size={13} weight="duotone" /> {form.specialization || "Add your specialty"}
                    </div>
                    {form.qualifications ? (
                        <div className="flex flex-wrap gap-1.5 pt-1.5" data-testid="profile-qual-chips">
                            {form.qualifications.split(",").map((q) => q.trim()).filter(Boolean).map((q) => (
                                <span
                                    key={q}
                                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-secondary border border-border text-foreground"
                                >
                                    {q}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                setTab("profile");
                                setOpenProfileAcc("personal");
                                // iter100.271 — the hero-card CTA lives at the top of
                                // the page; the Qualifications field is far below inside
                                // the "Personal & qualifications" accordion. Opening the
                                // accordion alone made it look like nothing happened.
                                // Now we scroll the field into view and focus it so the
                                // user's next keystroke lands where they expected.
                                setTimeout(() => {
                                    const el = document.querySelector('[data-testid="profile-qualifications"]');
                                    if (el) {
                                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                                        el.focus();
                                    }
                                }, 200);
                            }}
                            className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition pt-1"
                            data-testid="profile-hero-add-qual"
                        >
                            <Plus size={11} weight="bold" /> Add your qualifications
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => { setTab("profile"); setOpenProfileAcc("personal"); }}
                        className={`block w-full text-left text-xs italic pt-1.5 ${form.bio ? "text-muted-foreground" : "text-muted-foreground/60 hover:text-muted-foreground"}`}
                        data-testid="profile-hero-bio"
                    >
                        {form.bio
                            ? <span className="line-clamp-2">{form.bio}</span>
                            : "Add a short bio to personalise your patient-facing profile."}
                    </button>
                </div>

                {/* Completeness ring */}
                <div className="shrink-0 flex flex-col items-center gap-1.5" data-testid="profile-completeness-ring">
                    <CompletenessRing pct={completionPct} />
                    <div className="text-[10px] text-muted-foreground font-medium text-center leading-tight">Profile<br/>complete</div>
                </div>
            </div>

            {/* COMPLETENESS BANNER — only when below 100% */}
            {completionPct < 100 && (
                <div
                    className="rounded-xl bg-primary/10 border border-primary/30 px-4 py-3 flex items-center gap-3"
                    data-testid="profile-completeness-banner"
                >
                    <Info size={18} weight="duotone" className="text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-primary">Complete your profile to appear in patient search results</div>
                        <div className="text-[11px] text-primary/80 mt-0.5">
                            {overallCompletion.gaps > 0
                                ? `${overallCompletion.gaps} essential${overallCompletion.gaps === 1 ? "" : "s"} still missing. Fill in the highlighted section${overallCompletion.gaps === 1 ? "" : "s"} below to go live.`
                                : "Add the remaining details below to unlock full functionality."}
                        </div>
                    </div>
                    <div className="font-display text-xl font-bold text-primary tabular-nums shrink-0">{completionPct}%</div>
                </div>
            )}

            {/* iter100.175 — Refer & Earn moved out of the top-of-page
                inline panel into a proper tab (see PROFILE_SECTIONS →
                `refer` entry above). Cleaner IA + doctors already
                associate Profile tabs with settings-like personal
                actions. */}

            {/* TAB STRIP — solid teal pill style (matches /doctor/billing +
                /doctor/cases sub-tabs for visual grammar consistency). */}
            <div
                className="flex w-full bg-card border border-border rounded-xl p-1 gap-0.5 max-w-full overflow-x-auto"
                role="tablist"
                data-testid="profile-tab-strip"
            >
                {PROFILE_SECTIONS.map((s) => {
                    const active = tab === s.id;
                    return (
                        <button
                            key={s.id}
                            role="tab"
                            aria-selected={active}
                            onClick={() => setTab(s.id)}
                            data-testid={`profile-tab-${s.id}`}
                            className={`flex-1 min-w-0 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                                active
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                            }`}
                        >
                            <s.icon size={14} weight={active ? "fill" : "duotone"} />
                            <span className="truncate">{s.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* TAB CONTENT */}
            <div data-testid="profile-form" className="rounded-2xl border border-border bg-card p-6">
            {tab === "profile" && (
                <div className="space-y-6">
                    {/* iter100.322 — Grouped accordions by priority:
                        Required → Recommended → Optional. Each group has a
                        colored dot + eyebrow title + one-line description
                        so the doctor knows which accordions are essential
                        vs. nice-to-haves. Progress % lives in the hero
                        completeness ring above (single source of truth —
                        no duplicate bar here). */}

                    {/* ═══════ GROUP 1 · REQUIRED ═══════ */}
                    <div data-testid="profile-group-required">
                        <ProfileGroupHeader
                            tone="rose"
                            label="Required"
                            hint="Complete these to appear in patient search"
                        />
                        <div className="space-y-3">
                    <ExamAccordion
                        id="personal"
                        title="Personal & qualifications"
                        sub="Your name, credentials, fee, and signature"
                        filled={fills.personal.filled}
                        total={fills.personal.total}
                        icon={<UserIcon size={16} weight="duotone" />}
                        tone="indigo"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "personal"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "personal" : null)}
                    >
                        {/* iter100.323 — Split into 3 semantic sub-sections
                            per user's mockup: Basic Information (identity),
                            Professional Details (fee + experience + reg),
                            E-Signature (its own affordance-styled block). */}

                        {/* ─── SUB-SECTION A · Basic Information ─── */}
                        <ProfileSubSection
                            icon={<IdentificationCard size={14} weight="duotone" />}
                            label="Basic Information"
                        >
                            <div className="grid sm:grid-cols-2 gap-4">
                                <FieldLine label="Full name" chip={<LockedChip />}>
                                    <DoctorNameInput value={form.name} locked testid="profile-name" inputClassName="h-10" />
                                </FieldLine>
                                <FieldLine label="Specialization">
                                    <Input value={form.specialization} onChange={(e) => onField("specialization", e.target.value)} placeholder="e.g. Orthodontist" data-testid="profile-specialization" className="h-10" />
                                </FieldLine>
                                <FieldLine label="Qualifications">
                                    <Input value={form.qualifications} onChange={(e) => onField("qualifications", e.target.value)} placeholder="e.g. BDS, MDS (Orthodontics)" data-testid="profile-qualifications" className="h-10" />
                                </FieldLine>
                                <FieldLine label="Languages spoken" hint="Comma separated">
                                    <Input value={form.languages} onChange={(e) => onField("languages", e.target.value)} placeholder="English, Hindi, Marathi" data-testid="profile-languages" className="h-10" />
                                </FieldLine>
                            </div>
                        </ProfileSubSection>

                        {/* ─── SUB-SECTION B · Professional Details ─── */}
                        <ProfileSubSection
                            icon={<Briefcase size={14} weight="duotone" />}
                            label="Professional Details"
                        >
                            <div className="grid sm:grid-cols-2 gap-4">
                                <FieldLine label="Consultation fee (₹)">
                                    <Input
                                        value={form.consultation_fee}
                                        onChange={(e) => onField("consultation_fee", e.target.value.replace(/[^0-9]/g, ""))}
                                        inputMode="numeric" data-testid="profile-fee" className="h-10"
                                    />
                                </FieldLine>
                                <FieldLine
                                    label="Practicing since (year)"
                                    hint={(() => {
                                        const psy = Number(form.practice_start_year);
                                        const cy = new Date().getFullYear();
                                        if (psy >= 1950 && psy <= cy + 1) {
                                            const yrs = Math.max(0, cy - psy);
                                            return `≈ ${yrs} year${yrs === 1 ? "" : "s"} of experience`;
                                        }
                                        return "Experience count updates automatically";
                                    })()}
                                >
                                    <Input
                                        value={form.practice_start_year}
                                        onChange={(e) => onField("practice_start_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                                        inputMode="numeric"
                                        placeholder={String(new Date().getFullYear() - 10)}
                                        maxLength={4}
                                        data-testid="profile-practice-start-year"
                                        className="h-10"
                                    />
                                </FieldLine>
                                {/* DCI Registration is full-width in this sub-section
                                    per the mockup — it's a single high-signal
                                    credential that reads better on its own row. */}
                                <FieldLine
                                    label="DCI registration number"
                                    chip={form.dci_registration_number ? <LockedChip /> : null}
                                    hint={form.dci_registration_number ? null : "Your Dental Council of India registration ID"}
                                    fullWidth
                                >
                                    {form.dci_registration_number ? (
                                        <div className="flex items-center gap-2">
                                            <Input
                                                value={form.dci_registration_number}
                                                readOnly
                                                disabled
                                                data-testid="profile-dci-number"
                                                className="h-10 font-mono bg-slate-50 opacity-70 flex-1"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => toast.info("To update your DCI registration number, message Invisique support at hello@invisique.in — we lock this field so your verified-council badge stays authentic.")}
                                                className="h-10 shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                                                data-testid="profile-dci-locked-btn"
                                            >
                                                <Lock size={11} weight="fill" /> Locked
                                            </button>
                                        </div>
                                    ) : (
                                        <Input
                                            value={form.dci_registration_number}
                                            onChange={(e) => onField("dci_registration_number", e.target.value)}
                                            placeholder="e.g. 12345 / KAR"
                                            maxLength={40}
                                            className="h-10 font-mono"
                                            data-testid="profile-dci-number"
                                        />
                                    )}
                                </FieldLine>
                            </div>
                        </ProfileSubSection>

                        {/* ─── SUB-SECTION C · E-Signature ─── */}
                        <ProfileSubSection
                            icon={<PencilSimple size={14} weight="duotone" />}
                            label="E-Signature"
                        >
                            <div
                                id="profile-doctor-signature"
                                data-testid="profile-doctor-signature-anchor"
                                className="rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 p-4"
                            >
                                <p className="text-[12.5px] text-foreground/85 leading-snug mb-3">
                                    Your signature will appear on every prescription (Rx) and patient care card.{" "}
                                    <span className="font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
                                        Case submissions are blocked until you add one.
                                    </span>
                                </p>
                                <SignatureField
                                    value={form.doctor_signature_data_url || ""}
                                    onChange={(d) => onField("doctor_signature_data_url", d)}
                                    testid="profile-doctor-signature"
                                />
                            </div>
                        </ProfileSubSection>
                        {/* iter100.320 — Removed the standalone "About you"
                            callout that pointed to the Landing Page →
                            Design & Story tab. Redundant + noisy. */}
                    </ExamAccordion>

                    {/* iter100.322 — Credentials accordion moved out of
                        here into the Recommended group below. Certifications
                        aren't required to go live — they're a trust signal
                        on the microsite. */}

                    {/* iter100.40 — Contact + Business hours moved into the
                        Profile tab (was in the Hours & Contact tab, now
                        merged). They're patient-facing identity data, same
                        category as Personal & qualifications above. Order:
                        identity → operational → clinical → financial. */}
                    <ExamAccordion
                        id="contact"
                        title="Contact"
                        sub="How patients and our team reach you"
                        filled={fills.contact.filled}
                        total={fills.contact.total}
                        icon={<Phone size={16} weight="duotone" />}
                        tone="rose"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "contact"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "contact" : null)}
                    >
                        {/* iter100.324 — Contact accordion rebuilt as 3
                            section cards (Private / Public / Clinic
                            Address) with icons + right-aligned tag pills
                            + field-level helper hints BELOW inputs.
                            After-hours line gets an amber warning ring
                            to signal its emergency-only intent. */}

                        {/* ═══ CARD A · Private Contact ═══ */}
                        <ProfileFieldGroup
                            testid="profile-contact-group-private"
                            icon={<Lock size={16} weight="duotone" />}
                            label="Private Contact"
                            hint="For Invisique team only"
                            visibility="private"
                        >
                            <FieldLine
                                label="Login phone"
                                chip={<LockedChip />}
                                helper="Used for OTP login. Message support to change."
                            >
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 opacity-70 pointer-events-none select-none">
                                        <PhoneInput10 value={form.phone} onChange={() => {}} testid="profile-phone" inputClassName="h-10 bg-slate-50" />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => toast.info("To change your OTP login number, message Invisique support at hello@invisique.in — we lock this field so you never accidentally lose access to your account.")}
                                        className="h-10 shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                                        data-testid="profile-phone-locked-btn"
                                    >
                                        <Lock size={11} weight="fill" /> Locked
                                    </button>
                                </div>
                            </FieldLine>
                            <FieldLine
                                label="WhatsApp for account nudges"
                                helper="Case updates + product alerts land here. Blank = we'll use your login phone."
                            >
                                <PhoneInput10 value={form.whatsapp_phone} onChange={(v) => onField("whatsapp_phone", v)} testid="profile-whatsapp-phone" inputClassName="h-10" />
                            </FieldLine>
                            <FieldLine
                                label="Official email"
                                helper="Used for all official communications from Invisique."
                                fullWidth
                            >
                                <Input value={form.email} onChange={(e) => onField("email", e.target.value)} type="email" data-testid="profile-email" className="h-10" />
                            </FieldLine>
                        </ProfileFieldGroup>

                        {/* ═══ CARD B · Public Contact ═══ */}
                        <ProfileFieldGroup
                            testid="profile-contact-group-public"
                            icon={<DeviceMobile size={16} weight="duotone" />}
                            label="Public Contact"
                            hint="Shown to patients on your profile"
                            visibility="public"
                        >
                            <FieldLine
                                label="Phone for patients"
                                helper={form.landing_cta_phone ? "Primary Call button on your public microsite." : form.phone ? `Blank — currently reusing your login phone: ${form.phone}` : "Not set — no Call button will show on your public page."}
                            >
                                <PhoneInput10 value={form.landing_cta_phone} onChange={(v) => onField("landing_cta_phone", v)} testid="profile-landing-cta-phone" inputClassName="h-10" />
                            </FieldLine>
                            <FieldLine
                                label="WhatsApp for patients"
                                helper={form.landing_cta_whatsapp ? "Powers the WhatsApp button on your public microsite." : (form.whatsapp_phone || form.phone) ? `Blank — currently reusing ${form.whatsapp_phone || form.phone}` : "Not set — no WhatsApp button will show."}
                            >
                                <PhoneInput10 value={form.landing_cta_whatsapp} onChange={(v) => onField("landing_cta_whatsapp", v)} testid="profile-landing-cta-whatsapp" inputClassName="h-10" />
                            </FieldLine>
                            <FieldLine
                                label="After-hours emergency line"
                                helper={
                                    <span className="inline-flex items-start gap-1.5 text-amber-700">
                                        <Warning size={12} weight="fill" className="mt-0.5 shrink-0" />
                                        <span>High priority — shown on patient care cards. True emergencies only.</span>
                                    </span>
                                }
                                fullWidth
                            >
                                <Input
                                    value={form.after_hours_line}
                                    onChange={(e) => onField("after_hours_line", e.target.value)}
                                    placeholder="e.g. +91 9988776655 (after 10 pm, true emergencies only)"
                                    maxLength={160}
                                    className="h-10 border-amber-300 focus-visible:ring-amber-400/50 focus-visible:border-amber-400"
                                    data-testid="profile-after-hours-line"
                                />
                            </FieldLine>
                        </ProfileFieldGroup>

                        {/* ═══ CARD C · Clinic Address (with dark preview) ═══ */}
                        <ProfileFieldGroup
                            testid="profile-contact-group-clinic"
                            icon={<House size={16} weight="duotone" />}
                            label="Clinic Address"
                            hint="Appears on your profile, invoices, and PDFs"
                            visibility="public"
                            disableGrid
                            previewSlot={
                                <ClinicCardPreview
                                    clinicName={form.clinic_name}
                                    area={form.area}
                                    city={form.city}
                                    address={form.address}
                                    pincode={form.pincode}
                                    landingCtaPhone={form.landing_cta_phone}
                                    landingCtaWhatsapp={form.landing_cta_whatsapp}
                                    loginPhone={form.phone}
                                    whatsappPhone={form.whatsapp_phone}
                                />
                            }
                        >
                            <div className="space-y-4">
                                <FieldLine label="Primary clinic name">
                                    <Input value={form.clinic_name} onChange={(e) => onField("clinic_name", e.target.value)} data-testid="profile-clinic" className="h-10" placeholder="e.g. Bright Smiles Dental" />
                                </FieldLine>
                                <FieldLine label="Street address">
                                    <Textarea
                                        value={form.address}
                                        onChange={(e) => onField("address", e.target.value)}
                                        rows={2}
                                        data-testid="profile-address"
                                        placeholder="Shop 12, Main Road, near Big Bazaar"
                                    />
                                </FieldLine>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <FieldLine label="Area / Neighborhood">
                                        <Input
                                            value={form.area}
                                            onChange={(e) => onField("area", e.target.value)}
                                            data-testid="profile-area"
                                            className="h-10"
                                            placeholder="e.g. Bandra West"
                                            maxLength={60}
                                        />
                                    </FieldLine>
                                    <FieldLine label="City">
                                        <Input value={form.city} onChange={(e) => onField("city", e.target.value)} data-testid="profile-city" className="h-10" placeholder="e.g. Mumbai" />
                                    </FieldLine>
                                    <FieldLine label="Pincode">
                                        <Input
                                            value={form.pincode}
                                            onChange={(e) => onField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            data-testid="profile-pincode"
                                            className="h-10"
                                            placeholder="411009"
                                            inputMode="numeric"
                                            maxLength={6}
                                        />
                                    </FieldLine>
                                </div>
                            </div>
                        </ProfileFieldGroup>
                    </ExamAccordion>

                    <div id="business-hours" className="scroll-mt-24">
                    <ExamAccordion
                        id="hours"
                        title="Business hours"
                        sub={
                            fills.hours.openDays === 0
                                ? "No open days set — patients cannot book any appointments"
                                : `Open ${fills.hours.openDays} day${fills.hours.openDays === 1 ? "" : "s"} a week · drives your booking calendar`
                        }
                        filled={fills.hours.filled}
                        total={fills.hours.total}
                        icon={<Clock size={16} weight="duotone" />}
                        tone="amber"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "hours"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "hours" : null)}
                    >
                        {/* Real availability gate. If zero days are OPEN,
                            patients literally cannot book — surface a warning
                            banner so the doctor sees this before saving. */}
                        {fills.hours.openDays === 0 && (
                            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5 text-[13px] text-amber-900" data-testid="profile-hours-empty-warning">
                                <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Your clinic has no open days.</div>
                                    <div className="text-[12px] text-amber-900/85 leading-snug mt-0.5">
                                        Mark at least one day as OPEN below (e.g. Monday 10:00-18:00). Your booking calendar cannot accept any appointments while every day is closed.
                                    </div>
                                </div>
                            </div>
                        )}
                        <BusinessHoursEditor
                            value={form.business_hours}
                            onChange={(next) => onField("business_hours", next)}
                        />
                    </ExamAccordion>
                    </div>
                        </div> {/* /space-y-3 */}
                    </div> {/* /Required group */}

                    {/* ═══════ GROUP 2 · RECOMMENDED ═══════ */}
                    <div data-testid="profile-group-recommended">
                        <ProfileGroupHeader
                            tone="amber"
                            label="Recommended"
                            hint="Boost patient trust and prep cases faster"
                        />
                        <div className="space-y-3">
                    {/* iter100.319 — Certifications & Achievements.
                        Doctor-authored list; auto-badges (Invisique™
                        Certified + DCI Verified) render at the top from
                        server signals — locked. */}
                    <ExamAccordion
                        id="credentials"
                        title="Certifications & Achievements"
                        sub={
                            (form.credentials || []).length === 0
                                ? "Fellowships, awards, and publications you'd like patients to see"
                                : "Fellowships, awards, and publications you'd like patients to see"
                        }
                        icon={<Certificate size={16} weight="duotone" />}
                        tone="amber"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "credentials"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "credentials" : null)}
                        headerAccessory={
                            (form.credentials || []).length > 0 ? (
                                <span
                                    className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider"
                                    data-testid="cred-count-pill"
                                >
                                    {(form.credentials || []).length} added
                                </span>
                            ) : null
                        }
                    >
                        <CredentialsSection
                            credentials={form.credentials || []}
                            onChange={(next) => onField("credentials", next)}
                            invisiqueCertified={form._lifecycle_status === "published"}
                            dciRegNumber={form._dci_registration_number || ""}
                            stateCouncil={form._state_council || ""}
                        />
                    </ExamAccordion>

                    {/* iter97.80 — Intraoral scanning declaration.
                        iter100.36 — "Booking & fee" accordion deleted here.
                        Its two fields (consultation fee + years of experience)
                        were merged into Personal & Qualifications above. */}
                    <ExamAccordion
                        id="scanning"
                        title="Intraoral scanning"
                        sub="How your clinic captures 3D scans — helps us prep your cases correctly"
                        filled={fills.scanning.filled}
                        total={fills.scanning.total}
                        icon={<Scan size={16} weight="duotone" />}
                        tone="cyan"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "scanning"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "scanning" : null)}
                    >
                        <ScanningSection form={form} onField={onField} />
                    </ExamAccordion>
                        </div> {/* /space-y-3 */}
                    </div> {/* /Recommended group */}

                    {/* ═══════ GROUP 3 · OPTIONAL ═══════ */}
                    <div data-testid="profile-group-optional">
                        <ProfileGroupHeader
                            tone="sky"
                            label="Optional"
                            hint="Financial + brand polish — set up when you're ready"
                        />
                        <div className="space-y-3">
                    <ExamAccordion
                        id="billing"
                        title="Invoices & PDF branding"
                        sub="Everything printed on your invoices, prescriptions, and patient care cards"
                        filled={fills.billing.filled}
                        total={fills.billing.total}
                        icon={<Buildings size={16} weight="duotone" />}
                        tone="teal"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "billing"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "billing" : null)}
                        headerAccessory={
                            /* iter100.39 — Tiny live thumbnail of the
                               patient care card, always visible in the
                               accordion header (even collapsed). Doctors
                               can eyeball their branded-doc setup at a
                               glance without expanding. */
                            <MiniCareCardThumb
                                clinicName={form.clinic_letterhead || form.clinic_name}
                                clinicLogo={form.clinic_logo_data_url}
                            />
                        }
                    >
                        {/* iter100.68 — Rebuilt layout to match user's
                            HTML mockup: 2-col form grid for the identifier
                            fields, PDF-indicator strip, amber-tinted logo
                            callout, and a dedicated Member Card Preview
                            section header. Reuses `BrandingImageField` +
                            `ClinicPatientCardPreview` — no visual duplicate
                            components. */}
                        <div className="space-y-4">
                            {/* Row 1 — GSTIN + Letterhead side by side */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FieldLine label="GSTIN" hint="15-character GST identifier (optional)">
                                    <Input
                                        value={form.gstin}
                                        onChange={(e) => onField("gstin", e.target.value.toUpperCase())}
                                        placeholder="29ABCDE1234F1Z5"
                                        maxLength={15}
                                        data-testid="profile-gstin"
                                        className="h-10 font-mono"
                                    />
                                </FieldLine>

                                <FieldLine label="Clinic letterhead" hint="Prints at the top of every PDF">
                                    <Input
                                        value={form.clinic_letterhead}
                                        onChange={(e) => onField("clinic_letterhead", e.target.value)}
                                        placeholder="e.g. Bright Smiles Dental — Bandra"
                                        maxLength={120}
                                        className="h-10"
                                        data-testid="profile-clinic-letterhead"
                                    />
                                </FieldLine>
                            </div>

                            {/* Row 2 — Footer note full width */}
                            <FieldLine label="Invoice footer note" hint="Shown at the bottom of every printed invoice (payment terms, thank-you, etc.)">
                                <Textarea
                                    value={form.billing_footer}
                                    onChange={(e) => onField("billing_footer", e.target.value)}
                                    placeholder="Thank you for your continued trust. Payment due within 7 days."
                                    rows={2}
                                    data-testid="profile-billing-footer"
                                />
                            </FieldLine>

                            {/* Green PDF-indicator strip — mirrors the
                                mockup's "PDFs use: <letterhead>" chip. */}
                            <div
                                className="rounded-lg border-l-[3px] border-emerald-500 bg-emerald-50/70 px-3.5 py-2 text-[12px] text-emerald-900 flex items-center gap-2"
                                data-testid="profile-letterhead-effective"
                            >
                                <CheckCircle size={13} weight="fill" className="text-emerald-600 shrink-0" />
                                {form.clinic_letterhead ? (
                                    <span><span className="font-semibold">PDFs use:</span> {form.clinic_letterhead}</span>
                                ) : form.clinic_name ? (
                                    <span><span className="font-semibold">PDFs use:</span> {form.clinic_name} <span className="text-emerald-800/70">(from Locations)</span></span>
                                ) : (
                                    <span className="text-amber-800">No letterhead set — add one above to brand your PDFs</span>
                                )}
                            </div>

                            {/* Amber-tinted logo callout — matches the
                                mockup's dashed-border upload zone. Keeps
                                the reusable BrandingImageField internals
                                intact; wraps them in a themed container. */}
                            <div className="rounded-xl border-2 border-dashed border-amber-300/80 bg-gradient-to-br from-amber-50/70 to-orange-50/40 p-4">
                                <BrandingImageField
                                    label="Clinic logo"
                                    hint="Replaces the Invisique™ header on your PDFs. PNG or JPG, max 400×400 px (we resize automatically)."
                                    value={form.clinic_logo_data_url}
                                    onChange={(d) => onField("clinic_logo_data_url", d)}
                                    testid="profile-clinic-logo"
                                    maxDim={400}
                                    previewClass="bg-white max-h-24"
                                />
                            </div>

                            {/* Sky info line — auto-populated field origin. */}
                            <div className="rounded-xl bg-sky-50 border-l-[3px] border-sky-500 px-4 py-3 flex items-start gap-2.5 text-[12px] text-sky-900 leading-relaxed">
                                <Info size={14} weight="duotone" className="text-sky-600 shrink-0 mt-0.5" />
                                <div>
                                    Every printed document (invoices, prescriptions, care cards) auto-populates your clinic phone (from Hours &amp; Contact) and primary clinic address (from Locations). Update those to change what appears.
                                </div>
                            </div>
                        </div>

                        {/* Member Card Preview section — dedicated header
                            with LIVE indicator + real ClinicPatientCardPreview
                            component (single source of truth for what
                            patients actually see). */}
                        <div className="mt-6 pt-5 border-t border-slate-200/70">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-slate-800">
                                    Member Card Preview
                                </div>
                                <span
                                    className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-amber-950 shadow-sm"
                                    data-testid="profile-billing-live-badge"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                                    Live
                                </span>
                            </div>
                            <ClinicPatientCardPreview
                                clinicName={form.clinic_letterhead || form.clinic_name}
                                clinicLogo={form.clinic_logo_data_url}
                                clinicAddress={form.clinic_address || form.address}
                                clinicPhone={form.clinic_phone || form.phone}
                                clinicEmail={form.email}
                                memberSince={form._created_at}
                                showHeader={false}
                            />
                        </div>
                    </ExamAccordion>

                    <ExamAccordion
                        id="bank"
                        title="Bank & payouts"
                        sub="Where Invisique™ sends your clinic settlements"
                        filled={fills.bank.filled}
                        total={fills.bank.total}
                        icon={<Wallet size={16} weight="duotone" />}
                        tone="cyan"
                        bodyClass="bg-slate-50/60"
                        open={openProfileAcc === "bank"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "bank" : null)}
                    >
                        <div className="space-y-4 max-w-2xl">
                            <p className="text-xs text-muted-foreground bg-secondary/40 rounded-lg p-3">
                                These details are used by our finance team only — never shared with patients.
                                Updates take effect on your next payout cycle.
                            </p>
                            <FieldLine label="Account holder name">
                                <Input value={form.bank_account_name}
                                    onChange={(e) => onField("bank_account_name", e.target.value)}
                                    placeholder="As per bank records"
                                    className="h-10"
                                    data-testid="profile-bank-account-name" />
                            </FieldLine>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <FieldLine label="Account number">
                                    <Input value={form.bank_account_number}
                                        onChange={(e) => onField("bank_account_number", e.target.value)}
                                        placeholder="11-16 digits"
                                        className="h-10 font-mono"
                                        data-testid="profile-bank-account-number" />
                                </FieldLine>
                                <FieldLine label="IFSC code">
                                    <Input value={form.bank_ifsc}
                                        onChange={(e) => onField("bank_ifsc", e.target.value.toUpperCase())}
                                        placeholder="ABCD0123456"
                                        className="h-10 font-mono uppercase"
                                        data-testid="profile-bank-ifsc" />
                                </FieldLine>
                                <FieldLine label="Bank name">
                                    <Input value={form.bank_name}
                                        onChange={(e) => onField("bank_name", e.target.value)}
                                        placeholder="e.g. HDFC Bank"
                                        className="h-10"
                                        data-testid="profile-bank-name" />
                                </FieldLine>
                                <FieldLine label="UPI ID (optional)">
                                    <Input value={form.upi_id}
                                        onChange={(e) => onField("upi_id", e.target.value)}
                                        placeholder="clinic@upi"
                                        className="h-10 font-mono"
                                        data-testid="profile-upi-id" />
                                </FieldLine>
                            </div>
                        </div>
                    </ExamAccordion>
                        </div> {/* /space-y-3 */}
                    </div> {/* /Optional group */}

                    {/* iter72 — Phase B clinic branding. Drives the patient
                        care card PDF header + (future) email signatures +
                        invoice headers. All fields optional; blank = falls
                        back to the Invisique master brand. */}
                    {/* iter100.38 — Standalone "PDF branding" accordion
                        deleted; its two fields (Clinic letterhead + Clinic
                        logo) + the live ClinicPatientCardPreview were
                        folded into "Invoices & PDF branding" above. Same
                        content footprint, tighter feedback loop, one fewer
                        accordion to scan. */}
                </div>
            )}

            {tab === "hours_contact" && (
                <div className="space-y-2.5">
                    <ExamAccordion
                        id="contact"
                        title="Contact"
                        sub="How patients and our team reach you"
                        filled={fills.contact.filled}
                        total={fills.contact.total}
                        icon={<Phone size={16} weight="duotone" />}
                        tone="rose"
                        open={openHoursAcc === "contact"}
                        onOpenChange={(o) => setOpenHoursAcc(o ? "contact" : null)}
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <FieldLine label="Phone" hint="Main number — calls, SMS, account login">
                                <PhoneInput10 value={form.phone} onChange={(v) => onField("phone", v)} testid="profile-phone" inputClassName="h-10" />
                            </FieldLine>
                            <FieldLine label="WhatsApp number" hint="Leave blank to use the main phone. Used for nudges + shown on your public profile.">
                                <PhoneInput10 value={form.whatsapp_phone} onChange={(v) => onField("whatsapp_phone", v)} testid="profile-whatsapp-phone" inputClassName="h-10" />
                            </FieldLine>
                            <FieldLine label="Email">
                                <Input value={form.email} onChange={(e) => onField("email", e.target.value)} type="email" data-testid="profile-email" className="h-10" />
                            </FieldLine>
                            <FieldLine label="Primary clinic name">
                                <Input value={form.clinic_name} onChange={(e) => onField("clinic_name", e.target.value)} data-testid="profile-clinic" className="h-10" />
                            </FieldLine>
                            <FieldLine label="City">
                                <Input value={form.city} onChange={(e) => onField("city", e.target.value)} data-testid="profile-city" className="h-10" />
                            </FieldLine>
                        </div>
                        <div className="mt-4 space-y-4">
                            <FieldLine label="Primary clinic address">
                                <Textarea value={form.address} onChange={(e) => onField("address", e.target.value)} rows={2} data-testid="profile-address" />
                            </FieldLine>
                            {/* iter100.37 — After-hours contact absorbed here
                                from the (renamed) "PDF branding" accordion.
                                It's a contact number, not branding — belongs
                                with the other clinic contact fields. Prints
                                on the patient care-card footer for genuine
                                after-hours emergencies. */}
                            <FieldLine label="After-hours contact line" hint="Prints in orange at the bottom of the patient care card. For genuine emergencies only.">
                                <Input
                                    value={form.after_hours_line}
                                    onChange={(e) => onField("after_hours_line", e.target.value)}
                                    placeholder="e.g. +91 9988776655 (after 10 pm, true emergencies only)"
                                    maxLength={160}
                                    className="h-10"
                                    data-testid="profile-after-hours-line"
                                />
                            </FieldLine>
                        </div>
                    </ExamAccordion>

                    <ExamAccordion
                        id="hours"
                        title="Business hours"
                        sub={
                            fills.hours.openDays === 0
                                ? "No open days set — patients cannot book any appointments"
                                : `Open ${fills.hours.openDays} day${fills.hours.openDays === 1 ? "" : "s"} a week · drives your booking calendar`
                        }
                        filled={fills.hours.filled}
                        total={fills.hours.total}
                        icon={<Clock size={16} weight="duotone" />}
                        tone="amber"
                        open={openHoursAcc === "hours"}
                        onOpenChange={(o) => setOpenHoursAcc(o ? "hours" : null)}
                    >
                        {/* iter100.36 — Real availability gate lives here.
                            If zero days are OPEN, patients literally cannot
                            book — surface a warning banner so the doctor
                            sees this before saving. */}
                        {fills.hours.openDays === 0 && (
                            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5 text-[13px] text-amber-900" data-testid="profile-hours-empty-warning">
                                <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Your clinic has no open days.</div>
                                    <div className="text-[12px] text-amber-900/85 leading-snug mt-0.5">
                                        Mark at least one day as OPEN below (e.g. Monday 10:00-18:00). Your booking calendar cannot accept any appointments while every day is closed.
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Per-day-of-week hours editor. Used by the booking UI
                            to grey out out-of-hours slots and by the public
                            /doctor/profile page to display clinic hours. */}
                        <BusinessHoursEditor
                            value={form.business_hours}
                            onChange={(next) => onField("business_hours", next)}
                        />
                    </ExamAccordion>
                </div>
            )}

            {tab === "account" && (
                <div className="space-y-2.5">
                    <ExamAccordion
                        id="notifications"
                        title="Notification preferences"
                        sub="Which alerts you get, and where they go"
                        icon={<BellIcon size={16} weight="duotone" />}
                        tone="orange"
                        open={openAccountAcc === "notifications"}
                        onOpenChange={(o) => setOpenAccountAcc(o ? "notifications" : null)}
                    >
                        <NotificationPreferencesEditor
                            value={form.notification_preferences}
                            onChange={(next) => onField("notification_preferences", next)}
                        />
                    </ExamAccordion>

                    <ExamAccordion
                        id="security"
                        title="Security"
                        sub="Account protection & device sessions"
                        icon={<Lock size={16} weight="duotone" />}
                        tone="slate"
                        open={openAccountAcc === "security"}
                        onOpenChange={(o) => setOpenAccountAcc(o ? "security" : null)}
                    >
                        <SecurityPanel />
                    </ExamAccordion>
                </div>
            )}

            {tab === "locations" && (
                <Section title="Practice locations" hint="Branches where you see patients">
                    {/* Full inline CRUD — add, edit, set primary, deactivate,
                        delete — without leaving the Profile page. */}
                    <PracticeLocationsManager />
                </Section>
            )}

            {tab === "nudges" && (
                <Section
                    title="WhatsApp nudge templates"
                    hint="The 3 messages auto-prefilled when you tap the green WhatsApp button on the compliance dashboard"
                >
                    {/* Self-contained editor — manages its own state + API
                        calls. Doesn't share the parent Save footer because
                        each template is saved individually. */}
                    <NudgeTemplatesEditor />
                </Section>
            )}

            {tab === "team" && user?.role === "doctor" && (
                <Section title="Clinic staff" hint="Receptionists & assistants who help you manage patients">
                    {/* Full inline CRUD for clinic staff — add, edit, toggle
                        active. Used to live at /doctor/clinic-staff. */}
                    <ClinicStaffManager />
                </Section>
            )}

            {tab === "refer" && (
                <Section
                    title="Refer & Earn"
                    hint="Send a fellow provider into a fully-functional Invisique™ sandbox. When they onboard as a real provider, you get referral credit."
                >
                    {/* iter100.176 — "How it works" 4-step strip. Doctors
                        asked "so how do I actually earn?" — this makes
                        the sandbox → onboarding → credit flow tangible
                        at a glance and typically 2× share-rate on
                        referral panels. Numbered circles + short label
                        + tiny hint line; the dashed connectors make it
                        read as a journey, not four disconnected boxes. */}
                    <div
                        className="rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/5 via-white to-primary/5 p-5 mb-5"
                        data-testid="refer-how-it-works"
                    >
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-primary/80">
                                How it works
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                                Onboarding stage · earn once your colleague signs their first real case
                            </div>
                        </div>
                        <div className="relative grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-2">
                            {[
                                { n: 1, Icon: PaperPlaneTilt, title: "Share your link",     hint: "Generate below · WhatsApp, email, or SMS."       },
                                { n: 2, Icon: MagnifyingGlass, title: "They explore",       hint: "Full-portal sandbox · no signup, no risk."       },
                                { n: 3, Icon: UserPlus,        title: "They onboard",       hint: "Sign up as a real Invisique™ provider."          },
                                { n: 4, Icon: Gift,            title: "You earn credit",    hint: "Referral credit posts on their first real case." },
                            ].map((step, idx, arr) => (
                                <div key={step.n} className="relative flex flex-col items-center text-center px-2">
                                    {/* Dashed connector — desktop only, doesn't render after the last step. */}
                                    {idx < arr.length - 1 && (
                                        <div
                                            className="hidden sm:block absolute top-6 left-[calc(50%+22px)] right-[calc(-50%+22px)] border-t border-dashed border-primary/25 pointer-events-none"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <div className="relative h-12 w-12 rounded-full bg-white border-2 border-primary/25 grid place-items-center shadow-sm">
                                        <step.Icon size={18} weight="duotone" className="text-primary" />
                                        <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-white text-[10px] font-bold grid place-items-center">
                                            {step.n}
                                        </div>
                                    </div>
                                    <div className="mt-2 text-[12.5px] font-semibold text-slate-900">
                                        {step.title}
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-slate-500 leading-snug">
                                        {step.hint}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div data-testid="profile-refer-panel" className="scroll-mt-24">
                        {/* iter100.200 — Rewards tile above the link generator.
                            Shows the referral ledger the admin has approved
                            for this doctor + badges earned. */}
                        <ReferralRewardsCard />
                        <SandboxLinkGenerator
                            title="Refer a colleague"
                            description="Generate a magic URL that drops your colleague into a fully-functional Invisique™ portal — no signup needed. They get the sandbox to explore; you get referral credit when they onboard as a real provider."
                        />
                    </div>
                </Section>
            )}

            </div>
        </div>

        {/* iter98.32 — Sticky save bar pinned to the bottom. Shows live
            dirty state and runs the same save/cancel functions; visible
            only on the three form tabs (locations/team manage their own
            CRUD). Offset by the desktop sidebar width (md:left-64) so
            it doesn't overlap the rail. */}
        {FORM_TABS.has(tab) && (
            <div
                className="fixed bottom-0 left-0 md:left-64 right-0 bg-card border-t border-border px-6 py-3 flex items-center justify-between gap-3 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
                data-testid="profile-save-bar"
            >
                <div className="text-xs font-medium" data-testid="profile-save-status">
                    {isDirty ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-700">
                            <Warning size={13} weight="fill" /> Unsaved changes
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-primary">
                            <Check size={13} weight="bold" /> All saved
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={cancel}
                        disabled={busy || !isDirty}
                        className="rounded-full"
                        data-testid="profile-cancel"
                    >
                        Discard
                    </Button>
                    <Button
                        size="sm"
                        onClick={save}
                        disabled={busy || !isDirty}
                        className={`rounded-full min-w-[140px] transition-opacity ${busy ? "opacity-70 cursor-wait" : ""}`}
                        data-testid="profile-save"
                    >
                        {busy ? (
                            <>
                                <Spinner size={13} className="mr-1.5 animate-spin" weight="bold" />
                                Saving your changes…
                            </>
                        ) : (
                            <>
                                <FloppyDisk size={13} className="mr-1.5" weight="bold" />
                                Save changes
                            </>
                        )}
                    </Button>
                </div>
            </div>
        )}
        </>
    );
}


/* iter98.32 — Compact SVG completeness ring used in the profile hero.
   72px tube, 6px stroke, primary-colored progress arc on a muted track.
   `pct` is clamped to [0,100]; the centre text scales itself to fit. */
function CompletenessRing({ pct }) {
    const safePct = Math.max(0, Math.min(100, Math.round(pct || 0)));
    const r = 28;
    const C = 2 * Math.PI * r;
    const offset = C * (1 - safePct / 100);
    return (
        <div className="relative w-[72px] h-[72px]" data-testid="profile-completeness-ring-svg">
            <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
                <circle cx="36" cy="36" r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-border" />
                <circle
                    cx="36" cy="36" r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={C}
                    strokeDashoffset={offset}
                    className="text-primary transition-all"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-primary tabular-nums" data-testid="profile-completeness-pct">
                {safePct}%
            </div>
        </div>
    );
}

function Section({ id, title, hint, children }) {
    return (
        <section id={id} className="space-y-4 scroll-mt-24">
            <div className="border-b border-border/60 pb-1">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">{title}</div>
                {hint && <div className="text-[11px] text-muted-foreground/80 italic mt-0.5">{hint}</div>}
            </div>
            {children}
        </section>
    );
}

function FieldLine({ label, hint, chip, helper, children, fullWidth = false }) {
    return (
        <div className={fullWidth ? "sm:col-span-2" : ""}>
            <Label className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                <span>{label}</span>
                {chip}
                {hint && <span className="text-[10.5px] font-normal text-muted-foreground italic">· {hint}</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
            {helper && (
                <div className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
                    {helper}
                </div>
            )}
        </div>
    );
}


/* LockedChip — iter100.323
 * Small inline "🔒 Locked" pill rendered next to a FieldLine label to
 * signal that the field is uneditable. Complements the disabled-input
 * visual state — the chip is the primary signal, the greyed input
 * itself is the secondary. Click-target lives on the Locked BUTTON to
 * the right of the input, not on this chip (this is purely decorative).
 */
function LockedChip() {
    return (
        <span
            className="inline-flex items-center gap-1 rounded bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider"
            title="Contact Invisique support to change"
        >
            <Lock size={9} weight="fill" /> Locked
        </span>
    );
}


/* ProfileSubSection — iter100.323
 * Purpose: introduce a labelled sub-section inside a Profile accordion
 * (e.g. "Basic Information", "Professional Details", "E-Signature"
 * inside the Personal & Qualifications accordion). Uses a phosphor
 * icon + caps eyebrow + hairline divider — matches the flat document
 * pattern established by ProfileFieldGroup + SectionHeader in
 * CredentialsSection so all Profile accordion bodies read consistently.
 */
function ProfileSubSection({ icon, label, children }) {
    return (
        <section className="mb-7 last:mb-0" data-testid={`profile-subsection-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 pb-2 mb-4 border-b border-slate-200/80">
                {icon && (
                    <span className="text-slate-500 shrink-0">
                        {icon}
                    </span>
                )}
                <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">
                    {label}
                </span>
            </div>
            {children}
        </section>
    );
}


/* ProfileGroupHeader — iter100.322
 *
 * Colored dot + eyebrow title + one-line description used to introduce
 * a group of accordions (Required / Recommended / Optional) inside the
 * Profile tab. Tones map to a subtle brand palette:
 *   • rose  → REQUIRED  (must complete to go live)
 *   • amber → RECOMMENDED (trust signals + case prep)
 *   • sky   → OPTIONAL (financial + branding polish)
 *
 * Renders a small colored dot instead of an emoji per our design
 * guidelines ("Avoid emoji characters for icons").
 */
function ProfileGroupHeader({ tone = "rose", label, hint }) {
    const toneMap = {
        rose:  { dot: "bg-rose-500",  text: "text-rose-800",  hint: "text-rose-700/70"  },
        amber: { dot: "bg-amber-500", text: "text-amber-800", hint: "text-amber-700/70" },
        sky:   { dot: "bg-sky-500",   text: "text-sky-800",   hint: "text-sky-700/70"   },
    };
    const c = toneMap[tone] || toneMap.rose;
    return (
        <div className="flex items-center gap-2.5 pb-3" data-testid={`profile-group-header-${tone}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${c.dot} shadow-[0_0_0_3px_rgba(255,255,255,0.9)] shrink-0`} />
            <div className="min-w-0">
                <div className={`text-[11px] font-bold uppercase tracking-[0.18em] ${c.text}`}>
                    {label}
                </div>
                {hint && (
                    <div className={`text-[10.5px] mt-0.5 italic ${c.hint}`}>
                        {hint}
                    </div>
                )}
            </div>
        </div>
    );
}


/* ProfileFieldGroup — iter100.324 (rewrite)
 *
 * Purpose-driven section card used inside the Contact accordion for
 * Private / Public / Clinic Address subgroups. Structured as a proper
 * card with generous padding (per user's iter100.324 mockup) — the
 * card carries its own identity via icon + title + right-aligned tag
 * pill, so the doctor never wonders which subsection a field belongs
 * to.
 *
 * Composition:
 *   ┌─ Header row ─────────────────────────────────────┐
 *   │  [icon]  Title · hint          [visibility tag]  │
 *   └──────────────────────────────────────────────────┘
 *   Fields grid (or previewSlot 2-col layout)
 *
 * Sits on the parent accordion's slate canvas (`bg-slate-50/60`) as
 * a white card — the tint contrast between canvas + card is what
 * clearly delineates "one purpose per card" without needing extra
 * borders or nested containers.
 */
function ProfileFieldGroup({ icon, label, hint, testid, children, visibility, previewSlot, disableGrid }) {
    const visibilityConfig = {
        private: {
            label: "PRIVATE",
            hint: "Never shown publicly",
            className: "bg-slate-200/70 text-slate-700",
        },
        public: {
            label: "PATIENT-FACING",
            hint: "Shown to patients on your microsite",
            className: "bg-emerald-100 text-emerald-800",
        },
        clinic: {
            label: "ON INVOICES & PDFS",
            hint: "Prints on documents",
            className: "bg-amber-100 text-amber-800",
        },
    };
    const vis = visibility ? visibilityConfig[visibility] : null;
    const fieldsGrid = disableGrid
        ? <>{children}</>
        : <div className="grid sm:grid-cols-2 gap-x-4 gap-y-5">{children}</div>;
    return (
        <section
            className="mb-4 last:mb-0 rounded-xl bg-white border border-slate-200 p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            data-testid={testid}
        >
            {/* Header row */}
            <div className="flex items-start gap-3 pb-4 mb-4 border-b border-slate-100">
                {icon && (
                    <span className="shrink-0 w-8 h-8 rounded-lg bg-slate-100 grid place-items-center text-slate-600">
                        {icon}
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-[14.5px] font-bold text-foreground">
                            {label}
                        </span>
                        {hint && (
                            <span className="text-[11.5px] font-normal text-muted-foreground">
                                {hint}
                            </span>
                        )}
                    </div>
                </div>
                {vis && (
                    <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-widest ${vis.className}`}
                        title={vis.hint}
                        data-testid={`profile-group-visibility-${visibility}`}
                    >
                        {vis.label}
                    </span>
                )}
            </div>

            {previewSlot ? (
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
                    <div>{fieldsGrid}</div>
                    <div className="lg:sticky lg:top-4">{previewSlot}</div>
                </div>
            ) : (
                fieldsGrid
            )}
        </section>
    );
}


/* iter100.318 — Live "what patients see" preview card. Mirrors the
   `/find-doctor` card layout on the left (Clinic name row + Area, City
   row) and the Clinic tab format on the right (multi-line full address).
   Updates on every keystroke so the doctor sees exactly what their edit
   produces. */
/* ClinicCardPreview — iter100.324 (rewrite)
 *
 * Dark navy preview card matching the user's mockup — high-contrast
 * "here's what your patient sees" affordance. Renders inside the
 * Clinic Address section card of the Contact accordion.
 *
 * Composition:
 *   ┌── dark navy card ──────────────────────────────┐
 *   │  PATIENT VIEW PREVIEW  (caps, muted)           │
 *   │                                                 │
 *   │  Clinic name  (cyan/blue accent, bold)          │
 *   │  Street, Area, City                             │
 *   │  Pincode                                        │
 *   │  ─────────                                      │
 *   │  [📞 phone pill] [💬 whatsapp pill]             │
 *   │  ● Contact buttons active  (green dot + caption)│
 *   └────────────────────────────────────────────────┘
 *
 * The status dot at the bottom flips to amber and reads "No public
 * numbers set" when both landingCtaPhone and landingCtaWhatsapp are
 * blank (and no login/whatsapp fallback exists).
 */
function ClinicCardPreview({ clinicName, area, city, address, pincode, landingCtaPhone, landingCtaWhatsapp, loginPhone, whatsappPhone }) {
    const cleanCity = (city || "").trim();
    const cleanArea = ((area || "").trim())
        || ((address || "").split(",")[0].trim());
    const cleanAddress = (address || "").trim();
    const cleanPin = (pincode || "").trim();

    // Resolve effective public contact numbers (fallbacks to login/whatsapp
    // when the doctor hasn't set patient-facing numbers explicitly).
    const effectivePhone    = (landingCtaPhone    || "").trim() || (loginPhone   || "").trim();
    const effectiveWhatsapp = (landingCtaWhatsapp || "").trim() || (whatsappPhone || "").trim() || effectivePhone;
    const hasCallBtn = !!effectivePhone;
    const hasWhatsBtn = !!effectiveWhatsapp;
    const anyContact = hasCallBtn || hasWhatsBtn;

    // Build the location line pieces
    const locationLine = [cleanArea, cleanCity].filter(Boolean).join(", ");

    return (
        <div
            className="rounded-2xl bg-slate-900 text-slate-100 p-5 shadow-lg"
            data-testid="profile-clinic-preview"
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">
                Patient view preview
            </div>

            <div className="text-[15px] font-bold text-sky-300 leading-tight mb-1.5" data-testid="clinic-preview-name">
                {clinicName || <span className="text-slate-500 italic font-normal text-[13px]">Add your clinic name →</span>}
            </div>

            <div className="text-[13px] text-slate-300 leading-relaxed" data-testid="clinic-preview-address">
                {cleanAddress ? (
                    <>
                        {cleanAddress}
                        {(locationLine || cleanPin) && <br />}
                    </>
                ) : (
                    <span className="text-slate-500 italic">Add a street address →<br /></span>
                )}
                {locationLine && (
                    <>
                        {locationLine}
                        {cleanPin && <br />}
                    </>
                )}
                {cleanPin && <>PIN {cleanPin}</>}
            </div>

            {/* Contact buttons + status dot */}
            <div className="mt-4 pt-3 border-t border-slate-700/70">
                <div className="flex items-center gap-2 mb-2">
                    {hasCallBtn && (
                        <span className="w-8 h-8 rounded-lg bg-white/10 grid place-items-center text-white" title={`Call ${effectivePhone}`}>
                            <Phone size={15} weight="fill" />
                        </span>
                    )}
                    {hasWhatsBtn && (
                        <span className="w-8 h-8 rounded-lg bg-white/10 grid place-items-center text-white" title={`WhatsApp ${effectiveWhatsapp}`}>
                            <ChatCircleDots size={15} weight="fill" />
                        </span>
                    )}
                    {!anyContact && (
                        <span className="text-[11px] italic text-slate-500">No public numbers yet</span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                    <span className={`w-1.5 h-1.5 rounded-full ${anyContact ? "bg-emerald-400" : "bg-amber-400"}`} />
                    <span>{anyContact ? "Contact buttons active" : "Contact buttons hidden — add a number"}</span>
                </div>
            </div>
        </div>
    );
}


/* ─────────── Intraoral scanning section ───────────
 * iter97.80 — Captures whether the clinic has an in-house intraoral
 * scanner. Three primary states drive the inner UI:
 *
 *   YES         → reveal scanner detail fields (brand, model, count,
 *                 trained staff, export format, calibration cadence,
 *                 free-text notes). Used by the wizard (Step 3) to
 *                 pre-pick the matching format radio + by the lab to
 *                 calibrate downstream STL parsers.
 *   NO          → soft pitch for Invisique's on-site IO scanning
 *                 service. Doctor opts in via a single toggle which
 *                 flags `interested_in_io_scanning` for sales follow-up.
 *   CONSIDERING → softer "free TRIOS demo via Invisique" pitch with
 *                 the same opt-in toggle.
 *
 * All fields are optional / additive — no doctor is forced to declare
 * anything. */
const SCANNER_BRANDS = [
    "iTero", "3Shape TRIOS", "Medit", "Carestream", "Planmeca", "Other",
];
const SCAN_FORMATS = [
    { value: "stl",   label: "STL — universal" },
    { value: "ply",   label: "PLY — colour mesh" },
    { value: "obj",   label: "OBJ — textured" },
    { value: "multi", label: "Multi-format (lab picks)" },
];
const CALIB_CADENCES = [
    { value: "weekly",      label: "Weekly" },
    { value: "monthly",     label: "Monthly" },
    { value: "quarterly",   label: "Quarterly" },
    { value: "as_needed",   label: "As needed" },
];

function ScanningSection({ form, onField }) {
    const choice = form.in_house_scanner || "";
    const set = (v) => onField("in_house_scanner", v);

    return (
        <div className="space-y-6" data-testid="profile-scanning-section">
            {/* iter100.69 — Big decision box (matches user mockup).
                The single question that drives everything below:
                YES → scanner-config form; NO → Invisique service card. */}
            <div
                className="rounded-2xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/60 p-6"
                data-testid="profile-scanner-picker"
            >
                <div className="text-[15px] sm:text-[16px] font-extrabold tracking-tight text-slate-900 mb-4">
                    Do you have an intraoral scanner in your clinic?
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ScannerDecisionButton
                        active={choice === "yes"}
                        onClick={() => set("yes")}
                        icon={<CheckCircle size={18} weight="fill" />}
                        label="Yes — we have one"
                        accent="emerald"
                        testid="profile-scanner-yes"
                    />
                    <ScannerDecisionButton
                        active={choice === "no"}
                        onClick={() => set("no")}
                        icon={<PaperPlaneTilt size={18} weight="bold" />}
                        label="No — outsource needed"
                        accent="rose"
                        testid="profile-scanner-no"
                    />
                </div>
                {!choice && (
                    <p className="text-[12px] text-slate-500 italic mt-3 pl-0.5">
                        Pick one — your answer tunes the case wizard and unlocks the right next step.
                    </p>
                )}
            </div>

            {/* YES — Scanner Configuration */}
            {choice === "yes" && (
                <div
                    className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white overflow-hidden"
                    data-testid="profile-scanner-details"
                >
                    {/* Green header strip */}
                    <div className="bg-gradient-to-r from-emerald-100 to-emerald-200/60 border-b border-emerald-200 px-5 py-3.5">
                        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-teal-800 bg-white/95 px-3 py-1.5 rounded-full shadow-sm">
                            <Scan size={12} weight="duotone" /> Scanner Configuration
                        </span>
                    </div>
                    <div className="p-5 sm:p-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <FieldLine label="Scanner brand" hint="Primary device">
                                <select
                                    value={form.scanner_brand}
                                    onChange={(e) => onField("scanner_brand", e.target.value)}
                                    className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                                    data-testid="profile-scanner-brand"
                                >
                                    <option value="">Select a brand…</option>
                                    {SCANNER_BRANDS.map((b) => (
                                        <option key={b} value={b}>{b}</option>
                                    ))}
                                </select>
                            </FieldLine>
                            <FieldLine label="Model" hint="e.g. Element 5D, TRIOS 4">
                                <Input
                                    value={form.scanner_model}
                                    onChange={(e) => onField("scanner_model", e.target.value)}
                                    placeholder="Scanner model name"
                                    className="h-10"
                                    data-testid="profile-scanner-model"
                                />
                            </FieldLine>
                            <FieldLine label="Number of scanners">
                                <Input
                                    type="number" min={1} max={20}
                                    value={form.scanner_count}
                                    onChange={(e) => onField("scanner_count", e.target.value.replace(/[^0-9]/g, ""))}
                                    placeholder="1"
                                    inputMode="numeric"
                                    className="h-10"
                                    data-testid="profile-scanner-count"
                                />
                            </FieldLine>
                            <FieldLine label="Trained staff" hint="Certified operators">
                                <Input
                                    type="number" min={0} max={50}
                                    value={form.scanner_trained_staff_count}
                                    onChange={(e) => onField("scanner_trained_staff_count", e.target.value.replace(/[^0-9]/g, ""))}
                                    placeholder="0"
                                    inputMode="numeric"
                                    className="h-10"
                                    data-testid="profile-scanner-staff-count"
                                />
                            </FieldLine>
                            <FieldLine label="Default export format" hint="Pre-checked in wizard">
                                <select
                                    value={form.scanner_export_format}
                                    onChange={(e) => onField("scanner_export_format", e.target.value)}
                                    className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                                    data-testid="profile-scanner-format"
                                >
                                    <option value="">Select…</option>
                                    {SCAN_FORMATS.map((f) => (
                                        <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                </select>
                            </FieldLine>
                            <FieldLine label="Calibration cadence" hint="Recalibration frequency">
                                <select
                                    value={form.scanner_calibration_cadence}
                                    onChange={(e) => onField("scanner_calibration_cadence", e.target.value)}
                                    className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                                    data-testid="profile-scanner-calibration"
                                >
                                    <option value="">Select…</option>
                                    {CALIB_CADENCES.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </FieldLine>
                        </div>
                        <FieldLine label="Notes for the lab" hint="Anything planners should know">
                            <Textarea
                                value={form.scanner_notes}
                                onChange={(e) => onField("scanner_notes", e.target.value)}
                                placeholder="e.g. STL only, separate bite scan, auto-segments arches…"
                                rows={3}
                                data-testid="profile-scanner-notes"
                            />
                        </FieldLine>
                    </div>
                </div>
            )}

            {/* NO — subscribe to IO Scans by Invisique™ */}
            {choice === "no" && (
                <InvisiqueScanningServiceCard form={form} onField={onField} />
            )}
        </div>
    );
}

/* iter100.69 — Big decision-button card. Replaces the older pill-style
 * ScannerOption. Renders a full-width, gradient-tinted button that
 * doubles down on the emerald=yes / rose=no colour intent from the
 * mockup. Active state adds a coloured ring + bold shadow. */
function ScannerDecisionButton({ active, onClick, icon, label, accent, testid }) {
    const ACCENTS = {
        emerald: {
            on:  "border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100/80 text-emerald-800 shadow-md shadow-emerald-500/15 ring-1 ring-emerald-500/40",
            off: "border-slate-200 bg-white text-slate-600 hover:border-emerald-400 hover:text-emerald-800 hover:shadow-md hover:shadow-emerald-500/10 hover:-translate-y-0.5",
        },
        rose: {
            on:  "border-rose-500 bg-gradient-to-br from-rose-50 to-rose-100/80 text-rose-800 shadow-md shadow-rose-500/15 ring-1 ring-rose-500/40",
            off: "border-slate-200 bg-white text-slate-600 hover:border-rose-400 hover:text-rose-800 hover:shadow-md hover:shadow-rose-500/10 hover:-translate-y-0.5",
        },
    };
    const cls = ACCENTS[accent] || ACCENTS.emerald;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-pressed={active}
            className={`inline-flex items-center justify-center gap-2.5 px-5 py-4 rounded-2xl text-sm font-bold border-2 transition-all ${active ? cls.on : cls.off}`}
        >
            {icon}
            {label}
        </button>
    );
}

/* iter97.81 — IO Scans by Invisique card.
 *
 * Shown when the doctor declares NO in-house scanner. Replaces the older
 * "sales follow-up" lead-gen pitch with a true subscription model:
 *
 *   • Live per-scan fee is fetched from GET /api/settings/io-scanner-fee
 *     (admin-controlled, no doctor-side override). Default ₹3,000.
 *   • Doctor enables a single `Subscribe to this service` toggle. The
 *     backend stamps `io_scanning_subscribed_at` on the first true
 *     transition (display only — never reset).
 *   • Once subscribed, a small notes textarea collects dispatch
 *     preferences (best days, parking access, languages).
 *
 * The service is described in plain language: a trained Invisique
 * technician visits the clinic, captures every intraoral scan AND the
 * additional patient photographs required for case creation, and uploads
 * everything directly to the doctor's case wizard.
 */
function InvisiqueScanningServiceCard({ form, onField }) {
    const [fee, setFee] = useState(null);
    const subscribed = !!form.subscribed_to_io_scanning_service;

    useEffect(() => {
        let alive = true;
        api.get("/settings/io-scanner-fee")
            .then((r) => { if (alive) setFee(Number(r.data?.fee ?? 0)); })
            .catch(() => { if (alive) setFee(null); });
        return () => { alive = false; };
    }, []);

    const subscribedDate = form.io_scanning_subscribed_at
        ? new Date(form.io_scanning_subscribed_at).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
          }).replace(/ /g, "-").toUpperCase()
        : null;

    const feeAmount = fee != null ? `₹${fee.toLocaleString("en-IN")}` : "—";
    const feeReady  = fee != null;

    return (
        <div className="space-y-3" data-testid="profile-io-scanning-service-card">
            {/* HERO — compact pink gradient */}
            <div className="relative overflow-hidden rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 via-rose-100/50 to-pink-100/40 p-5">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-1/2 -right-[10%] w-[400px] h-[400px] rounded-full"
                    style={{ background: "radial-gradient(circle, rgba(244, 63, 94, 0.08) 0%, transparent 70%)" }}
                />
                <div className="relative">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-rose-200 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.06em] text-rose-600 shadow-sm mb-2.5">
                        <Sparkle size={11} weight="fill" /> Invisique™ Service
                    </div>
                    <h3 className="text-[19px] sm:text-[22px] font-display font-black text-slate-900 leading-tight mb-2">
                        IO Scans by Invisique™
                    </h3>
                    <p className="text-[12.5px] text-slate-600 leading-relaxed mb-3 max-w-2xl">
                        A trained Invisique™ technician visits your clinic, captures every intraoral scan,{" "}
                        <span className="font-semibold text-slate-900">and</span> takes the additional patient
                        photographs (intraoral + extraoral + smile). Everything uploads directly to your case
                        wizard — no equipment, no training, no learning curve.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                        {[
                            "Zero equipment investment",
                            "Certified technicians handle it all",
                            "Uploads straight into your wizard",
                        ].map((line) => (
                            <div key={line} className="flex items-center gap-2 text-[12px] font-semibold text-slate-700">
                                <span className="w-5 h-5 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                                    <Check size={10} weight="bold" className="text-emerald-600" />
                                </span>
                                {line}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* PRICING + VALUE — one horizontal card */}
            <div
                className="rounded-xl border border-teal-500/70 bg-white p-4"
                data-testid="profile-io-scanning-fee-strip"
            >
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3 pb-3 border-b border-dashed border-teal-200">
                    <div className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-slate-500">
                        <Wallet size={12} weight="duotone" className="text-teal-700" />
                        Per-scan charge
                    </div>
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-[26px] font-black text-slate-900 tabular-nums" data-testid="profile-io-scanning-fee-amount">
                            {feeAmount}
                        </span>
                        <span className="text-[12px] text-slate-500 font-semibold">
                            {feeReady ? "per scan" : "loading…"}
                        </span>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="profile-io-scanning-value-grid">
                    {[
                        { num: "₹0", label: "Equipment" },
                        { num: "0 hrs", label: "Staff Training" },
                        { num: "100%", label: "Case Ready" },
                    ].map((v) => (
                        <div key={v.label} className="rounded-lg bg-slate-50/70 border border-slate-200/70 py-2 px-3 text-center">
                            <div className="text-[16px] font-black text-teal-700 leading-none mb-1">{v.num}</div>
                            <div className="text-[9.5px] font-extrabold uppercase tracking-[0.04em] text-slate-500">{v.label}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-3 pt-3 border-t border-dashed border-teal-200 space-y-2" data-testid="profile-io-scanning-payment-terms">
                    <div className="flex items-start gap-2">
                        <IdentificationCard size={14} weight="duotone" className="text-teal-700 shrink-0 mt-0.5" />
                        <div className="text-[12px] text-slate-600 leading-snug">
                            <span className="font-bold text-slate-900">Payment:</span> upfront when booking, OR added to the case&apos;s final invoice — your choice, per case.
                        </div>
                    </div>
                    <div className="flex items-start gap-2">
                        <Warning size={14} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-[12px] text-slate-600 leading-snug">
                            <span className="font-bold text-slate-900">Non-refundable</span> if the case is cancelled at any stage.
                        </div>
                    </div>
                </div>
            </div>

            {/* HOW IT WORKS — compact teal callout */}
            <div
                className="rounded-lg border border-emerald-200 bg-gradient-to-br from-teal-50/80 to-emerald-50/70 border-l-[3px] border-l-teal-600 px-4 py-3"
                data-testid="profile-io-scanning-workflow-info"
            >
                <div className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.06em] text-teal-800 mb-1">
                    <Clock size={11} weight="duotone" /> How It Works
                </div>
                <p className="text-[12px] text-slate-800 leading-snug">
                    When you <span className="font-bold">create a new case</span>, you&apos;ll be asked to book a Scan Visit first. Pick a date, our technician arrives, and your case continues from there.
                </p>
            </div>

            {/* SUBSCRIBE BAR — compact */}
            <label
                className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50/60 px-4 py-3 cursor-pointer select-none hover:border-emerald-300 transition-colors"
                data-testid="profile-io-scanning-subscribe-label"
            >
                <div className="flex items-center gap-2.5">
                    <Switch
                        checked={subscribed}
                        onCheckedChange={(v) => onField("subscribed_to_io_scanning_service", v)}
                        data-testid="profile-io-scanning-subscribe-toggle"
                    />
                    <span className="text-[13px] font-bold text-slate-900">
                        Subscribe to IO Scans by Invisique™
                    </span>
                </div>
                {subscribed && (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                        data-testid="profile-io-scanning-subscribed-chip"
                    >
                        <Check size={10} weight="bold" />
                        {subscribedDate ? `Subscribed · ${subscribedDate}` : "Subscribed"}
                    </span>
                )}
            </label>
        </div>
    );
}


/* ─────────── BrandingImageField ───────────
 * Reusable file-picker that takes an image file, resizes it client-side
 * to `maxDim`, and stores it as a base64 data URL in parent state.
 * Used by Phase B (iter72) for the clinic logo + doctor signature fields.
 *
 * Why resize client-side: keeps the upload payload small (≤200 KB typical)
 * and avoids a separate file-upload endpoint. Same pattern as the patient
 * avatar / gallery upload.  */
function BrandingImageField({ label, hint, value, onChange, testid, maxDim = 400, previewClass = "" }) {
    const inputRef = React.useRef(null);

    const pick = () => inputRef.current?.click();
    const clear = () => onChange("");

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alertDialog({ title: "Please pick an image file", tone: "warn" });
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                let { width: w, height: h } = img;
                if (w > maxDim || h > maxDim) {
                    if (w >= h) { h = Math.round(h * (maxDim / w)); w = maxDim; }
                    else { w = Math.round(w * (maxDim / h)); h = maxDim; }
                }
                const canvas = document.createElement("canvas");
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                // PNG preserves transparency for signatures + sharp edges
                // for clinic logos. Worth the slight size hit vs JPEG.
                onChange(canvas.toDataURL("image/png"));
            };
            img.onerror = () => alertDialog({ title: "Couldn't read that image", tone: "warn" });
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
        if (inputRef.current) inputRef.current.value = "";
    };

    return (
        <div>
            <Label className="text-xs font-semibold flex items-center gap-1.5">
                {label}
                {hint && <span className="text-[10px] font-normal text-muted-foreground italic">· {hint}</span>}
            </Label>
            <div className="mt-1.5 flex items-start gap-3">
                {value ? (
                    <div className={`border border-border rounded-lg p-2 flex items-center justify-center ${previewClass}`} style={{ minWidth: 96, minHeight: 64 }}>
                        <img src={value} alt={label} className="max-h-20 max-w-[180px] object-contain" />
                    </div>
                ) : (
                    <div className="border border-dashed border-border rounded-lg flex items-center justify-center text-xs text-muted-foreground" style={{ width: 120, height: 64 }}>
                        No image
                    </div>
                )}
                <div className="flex flex-col gap-1.5">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFile}
                        className="hidden"
                        data-testid={`${testid}-file-input`}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={pick} className="rounded-full h-8 px-3 text-xs" data-testid={`${testid}-pick-btn`}>
                        {value ? "Replace" : "Upload"}
                    </Button>
                    {value && (
                        <Button type="button" variant="ghost" size="sm" onClick={clear} className="rounded-full h-8 px-3 text-xs text-rose-600 hover:bg-rose-50" data-testid={`${testid}-clear-btn`}>
                            Remove
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}



/* ─────────── Business hours editor ───────────
 * Week-grid table with per-day open/close inputs + a "Closed" toggle.
 * Updates flow back through onChange as the full hours dict — parent
 * sees a single merged object update on every keystroke (debounced is
 * unnecessary here; the parent only persists on Save).
 */
const DAYS_OF_WEEK = [
    { key: "mon", label: "Mon" },
    { key: "tue", label: "Tue" },
    { key: "wed", label: "Wed" },
    { key: "thu", label: "Thu" },
    { key: "fri", label: "Fri" },
    { key: "sat", label: "Sat" },
    { key: "sun", label: "Sun" },
];



/* MiniCareCardThumb — small (56×36 px) live thumbnail of the patient
   care card, rendered in the "Invoices & PDF branding" accordion header
   so doctors can eyeball their branded-doc setup at a glance without
   expanding the accordion. Reactive to letterhead / logo edits. */
function MiniCareCardThumb({ clinicName, clinicLogo }) {
    const initials = (() => {
        if (!clinicName) return "C";
        const words = String(clinicName).replace(/[,.()]/g, " ").trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) return "C";
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return (words[0][0] + words[1][0]).toUpperCase();
    })();
    return (
        <div
            className="hidden sm:flex items-center gap-1.5 h-9 px-2 rounded-md bg-white border border-border shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            title={clinicName ? `Preview: ${clinicName}` : "Preview: your clinic name will appear here"}
        >
            {clinicLogo ? (
                <img
                    src={clinicLogo}
                    alt=""
                    className="h-6 w-6 rounded object-contain bg-secondary/40 shrink-0"
                />
            ) : (
                <div className="h-6 w-6 rounded bg-teal-100 text-teal-800 text-[9px] font-bold uppercase tracking-tighter flex items-center justify-center shrink-0">
                    {initials}
                </div>
            )}
            <div className="min-w-0 leading-tight">
                <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500">Preview</div>
                <div className="text-[10.5px] font-semibold text-slate-700 truncate max-w-[110px]">
                    {clinicName || "Your clinic"}
                </div>
            </div>
        </div>
    );
}


function BusinessHoursEditor({ value, onChange }) {
    /* iter100.327 — Fix the "N of 7 days" phantom bug.
       Root cause: the UI defaulted MISSING days visually to OPEN
       9:00-18:00 (making all 7 rows look "set"), but only days the
       doctor actively touched were persisted. Dr. Ben Wilson's DB had
       only 3 keys (mon, sat, sun) even though the doctor believed
       they were open 6 days — because Tue-Fri kept their visual
       defaults and were never written to state.
       Fix:
       1. `hasData(day)` — a day is "set" only if it exists in value.
       2. Visually, unset days show a distinct "Set OPEN / CLOSED"
          state: neither pill highlighted, muted day label, "Not set"
          hint under the time selects.
       3. On ANY user interaction (any button click), we normalize
          the ENTIRE 7-day payload — merging the user's specific
          patch with sensible defaults for any still-untouched days.
          This ensures a single "Save" persists all 7 days.
       4. Added quick actions: "Copy Monday to weekdays" (Tue-Fri) +
          "Mark remaining as closed" — one-click completeness. */
    const days = DAYS_OF_WEEK;
    const hasData = (d) => Object.prototype.hasOwnProperty.call(value || {}, d);
    const setCount = days.filter((d) => hasData(d.key)).length;
    const unsetCount = 7 - setCount;

    // Normalize the entire payload — used by every update
    const buildFull = (patch) => {
        const next = {};
        days.forEach((d) => {
            const cur = value?.[d.key];
            if (cur && typeof cur === "object") {
                next[d.key] = {
                    open: cur.open || "09:00",
                    close: cur.close || "18:00",
                    closed: Boolean(cur.closed),
                };
            } else {
                // Not yet set — mark CLOSED by default (safe assumption; the
                // doctor can flip to OPEN in one click). Only applies to
                // days affected by the patch.
                next[d.key] = { open: "09:00", close: "18:00", closed: true };
            }
        });
        // Apply per-day patches
        Object.entries(patch || {}).forEach(([day, dayPatch]) => {
            next[day] = { ...next[day], ...dayPatch };
        });
        return next;
    };

    const update = (day, patch) => {
        onChange(buildFull({ [day]: patch }));
    };

    // Quick action: Copy Monday's hours to Tue-Fri
    const copyMondayToWeekdays = () => {
        const mon = value?.mon || { open: "09:00", close: "18:00", closed: false };
        onChange(buildFull({
            tue: { ...mon },
            wed: { ...mon },
            thu: { ...mon },
            fri: { ...mon },
        }));
    };

    // Quick action: mark all not-yet-set days as CLOSED
    const closeAllUnset = () => {
        const patch = {};
        days.forEach((d) => {
            if (!hasData(d.key)) patch[d.key] = { closed: true };
        });
        onChange(buildFull(patch));
    };

    return (
        <div className="space-y-3" data-testid="profile-business-hours">
            {/* iter100.325 — Sky-tinted info banner. */}
            <div className="rounded-xl bg-sky-50 border border-sky-200/70 px-4 py-3 flex items-start gap-2.5 text-[12.5px] text-slate-700 leading-relaxed">
                <Info size={14} weight="duotone" className="text-sky-600 shrink-0 mt-0.5" />
                <div>
                    Set your working hours for each day. Patients can only book during your OPEN hours — closed days are blocked entirely from the booking calendar.
                </div>
            </div>

            {/* iter100.327 — Amber nag banner when the doctor has
                untouched days. Explains what "not set" means +
                one-click bulk actions. */}
            {unsetCount > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="profile-hours-unset-banner">
                    <div className="flex items-start gap-2.5">
                        <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                        <div className="flex-1 text-[12.5px] text-amber-900 leading-relaxed">
                            <span className="font-bold">{unsetCount} day{unsetCount === 1 ? "" : "s"} not yet set</span> — your microsite will show &ldquo;By appointment&rdquo; for these days.
                            Pick OPEN or CLOSED for each row below, or use a quick action:
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2.5 ml-6">
                        <button
                            type="button"
                            onClick={copyMondayToWeekdays}
                            className="text-[11px] font-bold uppercase tracking-wider rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 px-3 py-1.5 transition-colors"
                            data-testid="profile-hours-copy-mon-btn"
                        >
                            Copy Mon → Tue-Fri
                        </button>
                        <button
                            type="button"
                            onClick={closeAllUnset}
                            className="text-[11px] font-bold uppercase tracking-wider rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 px-3 py-1.5 transition-colors"
                            data-testid="profile-hours-close-unset-btn"
                        >
                            Mark unset days as CLOSED
                        </button>
                    </div>
                </div>
            )}

            {/* Table container */}
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                {/* Table header */}
                <div className="hidden sm:grid grid-cols-[80px_1fr_1fr_180px] gap-3 items-center px-4 py-2.5 bg-slate-50/70 border-b border-slate-200 text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-500">
                    <div>Day</div>
                    <div>Open</div>
                    <div>Close</div>
                    <div className="text-right">Status</div>
                </div>

                {days.map((d, idx) => {
                    const isSet = hasData(d.key);
                    const row = value?.[d.key] || { open: "09:00", close: "18:00", closed: false };
                    const isClosed = isSet && !!row.closed;
                    const isOpen = isSet && !row.closed;
                    const inputsDim = !isSet || isClosed;
                    return (
                        <div
                            key={d.key}
                            className={`grid grid-cols-1 sm:grid-cols-[80px_1fr_1fr_180px] gap-3 sm:gap-3 items-center px-4 py-2.5 ${idx < days.length - 1 ? "border-b border-slate-100" : ""} ${!isSet ? "bg-amber-50/30" : ""}`}
                            data-testid={`profile-hours-${d.key}`}
                        >
                            <div className={`font-bold text-[13px] flex items-center gap-1.5 ${
                                !isSet ? "text-amber-800" : isClosed ? "text-slate-400" : "text-slate-900"
                            }`}>
                                {d.label}
                                {!isSet && (
                                    <span className="inline-flex items-center rounded-full bg-amber-200 text-amber-900 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest" title="Not yet saved">
                                        Not set
                                    </span>
                                )}
                            </div>
                            <div className={inputsDim ? "opacity-55 pointer-events-none" : ""}>
                                <TimeSlotSelect
                                    value={row.open || "09:00"}
                                    onChange={(v) => update(d.key, { open: v, closed: false })}
                                    minHour={6}
                                    maxHour={23}
                                    stepMins={30}
                                    className="h-10 text-sm"
                                    testId={`profile-hours-${d.key}-open`}
                                    disabled={inputsDim}
                                />
                            </div>
                            <div className={inputsDim ? "opacity-55 pointer-events-none" : ""}>
                                <TimeSlotSelect
                                    value={row.close || "18:00"}
                                    onChange={(v) => update(d.key, { close: v, closed: false })}
                                    minHour={6}
                                    maxHour={23}
                                    stepMins={30}
                                    className="h-10 text-sm"
                                    testId={`profile-hours-${d.key}-close`}
                                    disabled={inputsDim}
                                />
                            </div>
                            {/* OPEN/CLOSED toggle — third state: neither
                                highlighted when the day isn't yet set. */}
                            <div className="flex sm:justify-end">
                                <div
                                    role="tablist"
                                    aria-label={`${d.label} availability`}
                                    className={`inline-flex items-stretch rounded-full border p-0.5 w-full sm:w-[172px] ${
                                        !isSet ? "border-amber-300 bg-white" : "border-slate-200 bg-white"
                                    }`}
                                >
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={isOpen}
                                        onClick={() => update(d.key, { closed: false })}
                                        className={`flex-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors py-1.5 ${
                                            isOpen
                                                ? "bg-emerald-500 text-white shadow-sm"
                                                : "text-slate-500 hover:text-slate-700"
                                        }`}
                                        data-testid={`profile-hours-${d.key}-open-btn`}
                                    >
                                        Open
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={isClosed}
                                        onClick={() => update(d.key, { closed: true })}
                                        className={`flex-1 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors py-1.5 ${
                                            isClosed
                                                ? "bg-slate-400 text-white shadow-sm"
                                                : "text-slate-500 hover:text-slate-700"
                                        }`}
                                        data-testid={`profile-hours-${d.key}-closed-btn`}
                                    >
                                        Closed
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}



/* ─────────── Notification preferences editor ───────────
 * Topics × channels grid. The in_app channel defaults ON, email +
 * whatsapp default OFF for every topic. Doctor can override each cell
 * independently. Topic-level "all on/off" buttons make bulk-toggling
 * trivial. */
function NotificationPreferencesEditor({ value, onChange }) {
    const togglePref = (topic, channel) => {
        const current = value[topic] || { in_app: true, email: false, whatsapp: false };
        onChange({
            ...value,
            [topic]: { ...current, [channel]: !current[channel] },
        });
    };
    const setAllForTopic = (topic, on) => {
        onChange({
            ...value,
            [topic]: { in_app: on, email: on, whatsapp: on },
        });
    };
    return (
        <div className="space-y-2" data-testid="profile-notif-prefs">
            <div className="hidden sm:grid grid-cols-[1fr_60px_60px_60px_60px] gap-3 px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <div>Topic</div>
                <div className="text-center">In-app</div>
                <div className="text-center">Email</div>
                <div className="text-center">WhatsApp</div>
                <div className="text-right">All</div>
            </div>
            {NOTIFICATION_TOPICS.map((t) => {
                const row = value[t.key] || { in_app: true, email: false, whatsapp: false };
                return (
                    <div
                        key={t.key}
                        className="grid grid-cols-[1fr_60px_60px_60px_60px] gap-3 items-center bg-card border border-border rounded-xl px-3 py-2"
                        data-testid={`profile-notif-${t.key}`}
                    >
                        <div>
                            <div className="font-semibold text-sm">{t.label}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{t.hint}</div>
                        </div>
                        <div className="flex justify-center">
                            <Switch checked={!!row.in_app} onCheckedChange={() => togglePref(t.key, "in_app")} data-testid={`profile-notif-${t.key}-in_app`} />
                        </div>
                        <div className="flex justify-center">
                            <Switch checked={!!row.email} onCheckedChange={() => togglePref(t.key, "email")} data-testid={`profile-notif-${t.key}-email`} />
                        </div>
                        <div className="flex justify-center">
                            <Switch checked={!!row.whatsapp} onCheckedChange={() => togglePref(t.key, "whatsapp")} data-testid={`profile-notif-${t.key}-whatsapp`} />
                        </div>
                        <div className="flex justify-end gap-1">
                            <button
                                type="button"
                                onClick={() => setAllForTopic(t.key, true)}
                                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                data-testid={`profile-notif-${t.key}-all-on`}
                            >
                                On
                            </button>
                            <button
                                type="button"
                                onClick={() => setAllForTopic(t.key, false)}
                                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 hover:bg-rose-100"
                                data-testid={`profile-notif-${t.key}-all-off`}
                            >
                                Off
                            </button>
                        </div>
                    </div>
                );
            })}
            <p className="text-[10px] text-muted-foreground mt-2 pl-1">
                Email + WhatsApp delivery requires SMTP / WhatsApp Business credentials on the org. Speak to your account manager to enable.
            </p>
        </div>
    );
}


/* ─────────── Security panel ───────────
 * 2FA + active sessions surface. Both are placeholder stubs for now —
 * 2FA needs a TOTP integration playbook + provisioning UX before it
 * ships; active sessions need a server-side session-revoke endpoint
 * that doesn't exist yet. The empty UI is intentional so the IA + the
 * sidebar tab are in place when the underlying systems land.
 */
function SecurityPanel() {
    return (
        <div className="space-y-5 max-w-2xl">
            {/* 2FA */}
            <div className="bg-card border border-border rounded-xl p-4" data-testid="profile-security-2fa">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 grid place-items-center shrink-0">
                        <ShieldWarning size={18} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="font-bold text-sm">Two-factor authentication</div>
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                Coming soon
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Add an extra layer of security to your login with Google Authenticator / Authy.
                            Currently rolling out — your account manager will reach out when this is available for your clinic.
                        </p>
                    </div>
                </div>
            </div>

            {/* Active sessions */}
            <div className="bg-card border border-border rounded-xl p-4" data-testid="profile-security-sessions">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary/60 text-foreground/70 grid place-items-center shrink-0">
                        <Lock size={18} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="font-bold text-sm">Active sessions</div>
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                Coming soon
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            See and sign out other devices that are logged into your account.
                        </p>
                    </div>
                </div>
            </div>

            {/* Password / OTP */}
            <div className="bg-secondary/30 border border-border rounded-xl p-4">
                <div className="text-xs text-muted-foreground">
                    Your account uses OTP-based login — there&apos;s no password to manage. To change your registered phone number, contact your account manager.
                </div>
            </div>
        </div>
    );
}
